import { apiClient } from "../../lib/http/apiClient";
import { env } from "../../config/env";
import type {
  ActivityLoginsParams,
  ActivityLoginsResponse,
  ActivityEventsParams,
  ActivityEventsResponse,
  AdminLoginResponse,
  AdminPromptsResponse,
  AdminPromptRunRequest,
  AdminPromptRunResponse,
  AnalyticsSummary,
  Broadcast,
  BroadcastPreview,
  BroadcastRequest,
  CollectionStat,
  DashboardData,
  LeadContactOption,
  PaginatedResponse,
  PasswordRequestRow,
  SellerDeletedDealsResponse,
  SellerDeletionStat,
  SystemHealth,
  UserActivity,
  UserActivityOverview,
  UserActivityCategoryResponse,
  SessionConversationResponse,
  UserMarketCoverage,
  NotifOpsHealth,
  NotifOpsDeviceOwner,
  NotifOpsUserDevices,
  NotifOpsDelivery,
  NotifOpsDailyRow,
  NotifOpsDuplicate,
  NotifOpsBroadcastRecipients,
  NotifOpsRange,
  NotificationCatalogEntry,
  NotificationTemplateUpdate,
  UserNotificationStatus,
  UserNotificationSettings,
  UserNotificationSettingsUpdate,
  AdminTemplate,
  AdminTemplateField,
  AdminTemplateCategory,
  AdminUserAssetsResponse,
  AdminTemplateFromAssetDraft,
  CommunityAudiencePreview,
  CommunityEngagement,
  AdminMarketReviewNotification,
  DealerReviewsData,
  AiGenerationsResponse,
  DealFlyerGenerationsResponse,
  AiSearchUsageResponse,
  MarketPulseFilterOptions,
  MarketPulseSummary,
  MarketPulseTableResponse,
  GeoRollupLevel,
  GeoRollupResponse,
  MarketPulseFilters,
  PriceTrendResponse,
  PriceTrendFilters,
  StuckShadowDealDetail,
  RedisPoolSizeResponse,
  AllRedisPoolSizesResponse,
  RedisMaxclientsResponse,
  DealPineconeStatus,
  RagIndexingConfig,
  RagFailedDeal,
  RagMissingScanResponse,
  RagReindexAllStatus,
  GalleryItem,
  WebsocketConnectionsResponse,
  S3BackupStatus,
  RealtimeOverviewResponse,
  RedisOverviewResponse,
  RedisNamespacesResponse,
  RedisClientsResponse,
  RedisQueuesResponse,
  RecRedisConnectionsResponse,
  RecWsHealthResponse,
  RecJobHealthResponse,
  RecRedisOverviewResponse,
  RecFallbackHealthResponse,
  CdnRedisOverviewResponse,
  CdnQueueStatusResponse,
  GstVerificationJobSummary,
  GstVerificationJobDetail,
  GstVerificationDetails,
  HelpKbTarget,
  HelpKbStatus,
  HelpKbReindexResult,
  OrgAdminMagicLinkResponse,
  OrgCandidateSearchResponse,
  OrgCandidateUser,
  OrganizationCreateRequest,
  OrganizationPatchRequest,
  OrganizationSummary
} from "./types";

type TemplateAssetType = "flyer" | "video" | "itinerary";

export { adminStorage } from "../../lib/storage/adminStorage";
import { adminStorage } from "../../lib/storage/adminStorage";

export function adminHeaders() {
  const token = adminStorage.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── Inactive-user WhatsApp nudge types ──────────────────────────────────────
export interface InactivityNudgeConfig {
  mode: "shadow" | "dry_run" | "live";
  enabled: boolean;
  run_interval_hours: number;
  inactivity_hours: number;
  max_age_days: number;
  segments: Record<string, boolean>;
  roles: string[];
  send_window: { start: string; end: string; tz: string };
  per_run_cap: number;
  daily_cap: number;
  holdout_pct: number;
  presence_guard_minutes: number;
  touch_gap_hours: number;
  require_explicit_consent: boolean;
  allowed_country_codes: string[];
  templates: Record<string, { name: string; language: string }>;
  updated_by?: string | null;
  updated_at?: string | null;
}

export interface InactivityNudgeRow {
  user_id: string;
  name: string;
  group: string | null;
  group_meaning: string;
  status: string;
  reason: string | null;
  status_sentence: string;
  why: {
    group: string | null;
    anchor_at: string | null;
    signed_up_at: string | null;
    last_real_action: string | null;
    last_real_action_at: string | null;
    consent: string;
    resume_text?: string | null;
  };
  resume_text?: string | null;
  whatsapp?: {
    template: string;
    language: string;
    variables: string[];
    message: string | null;
  } | null;
  fcm?: { title: string | null; body: string | null } | null;
}

export interface InactivityNudgePreview {
  mode: string;
  mode_text: string;
  enabled: boolean;
  generated_at: string;
  summary: {
    cohort_size: number;
    by_status: Record<string, number>;
    by_reason: Record<string, number>;
    idle_by_segment: Record<string, number>;
    idle_A: number;
    idle_B: number;
  };
  legend: {
    status: Record<string, string>;
    reason: Record<string, string>;
    segment: Record<string, string>;
  };
  rows: InactivityNudgeRow[];
}

export interface InactivityNudgeRun {
  run_id: string;
  started_at: string;
  finished_at: string;
  mode: string;
  cohort_size: number;
  counts: Record<string, number>;
  reasons: Record<string, number>;
  idle_A: number;
  idle_B: number;
}

// Per-domain storage mode (backend: app/core/storage_backend.py `Backend`).
//   s3                 -> bytes in S3, served as a presigned S3 URL
//   bunny_storage      -> bytes in Bunny Storage, served from the pull zone
//   s3_via_bunny_cache -> bytes stay in S3, served through the Bunny pull zone
//                         whose origin is that bucket (nothing is copied, so it
//                         applies to existing objects instantly)
// The legacy value "bunny" is normalized to "bunny_storage" by the API.
export type StorageBackendMode = "s3" | "bunny_storage" | "s3_via_bunny_cache";

// One backend's circuit-breaker state (backend: app/modules/admin/storage_health_routes.py).
// `unhealthy_until` is a time.monotonic() stamp from the API process — only
// useful as "still cooling down", never as a wall-clock time.
export type StorageBackendHealth = {
  reachable: boolean;
  unhealthy_until: number | null;
};

// Result of a one-key live delivery probe (backend: GET
// /api/admin/storage-domains/{domain}/cache-check). `mode` is the ACTUAL
// resolved read mode for the sampled key — compare it against the domain's
// configured mode to catch a config/behavior mismatch, not just "is it up".
export type StorageDomainCacheCheck = {
  domain: string;
  sample_key: string | null;
  mode?: StorageBackendMode;
  resolved_url?: string;
  resolved_host?: string;
  reachable: boolean | null;
  status_code?: number | null;
  error?: string | null;
  message?: string;
};

// One queued per-domain migration run, as the backend's job document
// (backend: app/modules/admin/storage_migration_repository.py). The worker
// walks the domain's collections 500 documents at a time and folds each
// window's counters into `progress`, so polling this shows live movement.
export type StorageMigrationJobStatus = "queued" | "in_progress" | "completed" | "failed";

export type StorageMigrationJob = {
  job_id: string;
  domain: string;
  collections: string[];
  status: StorageMigrationJobStatus;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  started_at: string | null;
  finished_at: string | null;
  // Resume checkpoint: the next document window the worker will scan. Only
  // meaningful as "how far in it is", not as a count of anything.
  next_skip: number;
  requeue_count: number;
  // Terminal failure of the whole job (domain re-routed mid-run, or the stale
  // sweep gave up). Per-key failures live in `progress.errors` instead and do
  // not fail the job.
  error: string | null;
  progress: {
    scanned_documents: number;
    scanned_keys: number;
    already_present_at_target: number;
    copied: number;
    rewritten_documents: number;
    rewritten_fields: number;
    // Capped sample of the per-key failures; `errors_total` is the real count.
    errors: {
      key: string;
      stage: string;
      error: string;
      collection?: string;
      document_id?: string;
    }[];
    errors_total: number;
  };
};

export const adminService = {
  async login(email: string, password: string): Promise<AdminLoginResponse> {
    const { data } = await apiClient.post<AdminLoginResponse>("/api/admin/login", { email, password });
    adminStorage.setSession(data.access_token, data.user);
    return data;
  },

  logout() {
    localStorage.clear();
    sessionStorage.clear();
  },

  async getDashboard(): Promise<DashboardData> {
    const { data } = await apiClient.get<DashboardData>("/api/admin/dashboard", { headers: adminHeaders() });
    return data;
  },

  // Users
  async getUsers(params: Record<string, any> = {}): Promise<PaginatedResponse> {
    const { data } = await apiClient.get<PaginatedResponse>("/api/admin/users", { params, headers: adminHeaders() });
    return data;
  },

  /** Distinct non-empty company/brand/agency names across sellers — feeds the
   * "group by company" filter dropdown on Users, Deals, and Enquiries. */
  async getCompanyNames(): Promise<string[]> {
    const { data } = await apiClient.get<{ company_names: string[] }>("/api/admin/company-names", { headers: adminHeaders() });
    return data.company_names;
  },

  /** Distinct buyers/sellers who actually appear in `leads` (not every user
   * in the system) — feeds the Enquiries page's Buyer/Seller name dropdowns,
   * so admins pick a real sender/recipient (also filterable by email/phone
   * via the label) instead of typing free text. */
  async getLeadBuyerOptions(): Promise<LeadContactOption[]> {
    const { data } = await apiClient.get<{ options: LeadContactOption[] }>("/api/admin/leads/buyer-options", { headers: adminHeaders() });
    return data.options;
  },
  async getLeadSellerOptions(): Promise<LeadContactOption[]> {
    const { data } = await apiClient.get<{ options: LeadContactOption[] }>("/api/admin/leads/seller-options", { headers: adminHeaders() });
    return data.options;
  },
  async getUser(id: string): Promise<any> {
    const { data } = await apiClient.get(`/api/admin/users/${id}`, { headers: adminHeaders() });
    return data;
  },
  /** POST /api/admin/users/{id}/change-number — move a user's locked account
   *  (WhatsApp/login) number. The only route that can: every self-service path
   *  is rejected with 409 ACCOUNT_NUMBER_LOCKED, which is what sends users to
   *  admin in the first place (they file the ask via
   *  POST /api/v1/profile/request-number-change, which shows up on the bell as
   *  `phone_change_requested` and on the user doc as
   *  `pending_phone_change_request`).
   *
   *  No OTP — the number is validated against its own country's rules and
   *  checked for uniqueness. A number already held by another account answers
   *  409 with `detail.conflicting_user` ({id, name, email}) so the caller can
   *  name it. On success the change is appended to `phone_number_history` and
   *  the pending request is cleared. */
  async changeUserNumber(
    id: string,
    body: {
      new_phone_country_code: string;
      new_phone_number: string;
      new_whatsapp_country_code?: string;
      new_whatsapp_number?: string;
      reason?: string;
      notify_user: boolean;
    },
  ): Promise<{ status: string; phone: string; whatsapp: string; history_entry: Record<string, any> }> {
    const { data } = await apiClient.post(`/api/admin/users/${id}/change-number`, body, { headers: adminHeaders() });
    return data;
  },
  async getUserActivity(id: string, params: Record<string, any> = {}): Promise<UserActivity> {
    const { data } = await apiClient.get<UserActivity>(`/api/admin/users/${id}/activity`, { params, headers: adminHeaders() });
    return data;
  },
  async getUserActivityOverview(id: string): Promise<UserActivityOverview> {
    const { data } = await apiClient.get<UserActivityOverview>(`/api/admin/users/${id}/activity-overview`, { headers: adminHeaders() });
    return data;
  },
  async getUserMarketCoverage(id: string): Promise<UserMarketCoverage> {
    const { data } = await apiClient.get<UserMarketCoverage>(`/api/admin/users/${id}/market-coverage`, { headers: adminHeaders() });
    return data;
  },
  async approveUserPendingMarket(id: string, countryCode: string): Promise<UserMarketCoverage> {
    const { data } = await apiClient.post<UserMarketCoverage>(`/api/admin/users/${id}/market-coverage/approve/${countryCode}`, {}, { headers: adminHeaders() });
    return data;
  },
  async addSellerMarket(userId: string, country: string, opts?: { states?: string[]; cities?: string[] }): Promise<UserMarketCoverage> {
    const { data } = await apiClient.post<UserMarketCoverage>(
      `/api/admin/users/${userId}/market`,
      { country, states: opts?.states, cities: opts?.cities },
      { headers: adminHeaders() },
    );
    return data;
  },
  async disapproveSellerMarket(userId: string, country: string, opts?: { states?: string[]; cities?: string[] }): Promise<UserMarketCoverage> {
    const { data } = await apiClient.post<UserMarketCoverage>(
      `/api/admin/users/${userId}/market/disapprove`,
      { country, states: opts?.states, cities: opts?.cities },
      { headers: adminHeaders() },
    );
    return data;
  },
  async approveDisapprovedMarket(userId: string, country: string, opts?: { states?: string[]; cities?: string[] }): Promise<UserMarketCoverage> {
    const { data } = await apiClient.post<UserMarketCoverage>(
      `/api/admin/users/${userId}/market/approve`,
      { country, states: opts?.states, cities: opts?.cities },
      { headers: adminHeaders() },
    );
    return data;
  },
  async disapproveSellerMarketRegion(userId: string, regionKey: string): Promise<UserMarketCoverage> {
    const { data } = await apiClient.post<UserMarketCoverage>(
      `/api/admin/users/${userId}/market/region/disapprove`,
      { region_key: regionKey },
      { headers: adminHeaders() },
    );
    return data;
  },
  async approveDisapprovedMarketRegion(userId: string, regionKey: string): Promise<UserMarketCoverage> {
    const { data } = await apiClient.post<UserMarketCoverage>(
      `/api/admin/users/${userId}/market/region/approve`,
      { region_key: regionKey },
      { headers: adminHeaders() },
    );
    return data;
  },
  async approvePendingMarketRegion(userId: string, regionKey: string, countryCodes?: string[]): Promise<UserMarketCoverage> {
    const { data } = await apiClient.post<UserMarketCoverage>(
      `/api/admin/users/${userId}/market/region/approve-pending`,
      { region_key: regionKey, country_codes: countryCodes },
      { headers: adminHeaders() },
    );
    return data;
  },
  async getPendingMarketReviewNotifications(): Promise<{ notifications: AdminMarketReviewNotification[]; total: number; unread_count: number }> {
    const { data } = await apiClient.get("/api/notifications", {
      params: { notif_type: "seller_market_pending_review", unread_only: true, limit: 20 },
      headers: adminHeaders(),
    });
    return data;
  },
  async markNotificationRead(id: string): Promise<void> {
    await apiClient.patch(`/api/notifications/${id}/read`, {}, { headers: adminHeaders() });
  },
  /** Durable, never-deleted log of every notification sent to admins
   * (pending-verification alerts, verification requests, etc.) — see
   * record_admin_notification_log in backend/app/modules/notifications/service.py. */
  async getAdminNotificationsLog(params: { page?: number; page_size?: number } = {}): Promise<{
    items: { _id: string; type: string; title: string; body: string; metadata?: Record<string, any>; source_module?: string; created_at: string }[];
    total: number;
    page: number;
    page_size: number;
  }> {
    const { data } = await apiClient.get("/api/admin/notifications-log", { params, headers: adminHeaders() });
    return data;
  },
  async getUserActivityCategory(id: string, category: string, params: Record<string, any> = {}): Promise<UserActivityCategoryResponse> {
    const { data } = await apiClient.get<UserActivityCategoryResponse>(`/api/admin/users/${id}/activity/${category}`, { params, headers: adminHeaders() });
    return data;
  },
  /** Read-only transcript of one of the user's chat sessions (audited server-side). */
  async getUserSessionConversation(
    userId: string,
    sessionId: string,
    params: { cursor?: string | null; limit?: number; reason?: string } = {},
  ): Promise<SessionConversationResponse> {
    const { data } = await apiClient.get<SessionConversationResponse>(
      `/api/admin/users/${encodeURIComponent(userId)}/conversations/${encodeURIComponent(sessionId)}`,
      { params: { limit: params.limit, ...(params.cursor ? { cursor: params.cursor } : {}), ...(params.reason ? { reason: params.reason } : {}) }, headers: adminHeaders() },
    );
    return data;
  },
  async getUserDmConversation(
    userId: string,
    threadId: string,
    params: { cursor?: string | null; limit?: number; reason?: string } = {},
  ): Promise<SessionConversationResponse> {
    const { data } = await apiClient.get<SessionConversationResponse>(
      `/api/admin/users/${encodeURIComponent(userId)}/dm-threads/${encodeURIComponent(threadId)}/messages`,
      { params: { limit: params.limit, ...(params.cursor ? { cursor: params.cursor } : {}), ...(params.reason ? { reason: params.reason } : {}) }, headers: adminHeaders() },
    );
    return data;
  },
  async getEnquiryConversation(
    enquiryId: string,
    params: { cursor?: string | null; limit?: number; thread_id?: string } = {},
  ): Promise<SessionConversationResponse> {
    const { data } = await apiClient.get<SessionConversationResponse>(
      `/api/admin/enquiries/${encodeURIComponent(enquiryId)}/conversation`,
      {
        params: {
          limit: params.limit,
          ...(params.cursor ? { cursor: params.cursor } : {}),
          ...(params.thread_id ? { thread_id: params.thread_id } : {}),
        },
        headers: adminHeaders(),
      },
    );
    return data;
  },
  async getUserDeletedSessions(userId: string, params: { page?: number; page_size?: number } = {}): Promise<{
    total?: number;
    sessions: {
      session_id: string;
      source: string;
      created_at: string;
      last_message_at: string;
      message_count: number;
      archived_at: string;
    }[];
  }> {
    const { data } = await apiClient.get(`/api/admin/users/${userId}/deleted-sessions`, { headers: adminHeaders(), params });
    return data ?? { sessions: [], total: 0 };
  },
  async getNotificationCatalog(): Promise<{ items: NotificationCatalogEntry[] }> {
    const { data } = await apiClient.get<{ items: NotificationCatalogEntry[] }>("/api/admin/notifications/catalog", { headers: adminHeaders() });
    return data;
  },
  async getUserNotificationStatus(id: string): Promise<UserNotificationStatus> {
    const { data } = await apiClient.get<UserNotificationStatus>(`/api/admin/users/${id}/notification-status`, { headers: adminHeaders() });
    return data;
  },
  async updateNotificationTemplate(notifType: string, payload: NotificationTemplateUpdate): Promise<{ items: NotificationCatalogEntry[] }> {
    await apiClient.put(`/api/admin/notifications/catalog/${encodeURIComponent(notifType)}`, payload, { headers: adminHeaders() });
    return adminService.getNotificationCatalog();
  },
  async createNotificationTemplate(payload: any): Promise<{ items: NotificationCatalogEntry[] }> {
    await apiClient.post(`/api/admin/notifications/catalog`, payload, { headers: adminHeaders() });
    return adminService.getNotificationCatalog();
  },
  async deleteNotificationTemplate(notifType: string): Promise<{ items: NotificationCatalogEntry[] }> {
    await apiClient.delete(`/api/admin/notifications/catalog/${encodeURIComponent(notifType)}`, { headers: adminHeaders() });
    return adminService.getNotificationCatalog();
  },
  async getUserNotificationSettings(id: string): Promise<UserNotificationSettings> {
    const { data } = await apiClient.get<UserNotificationSettings>(`/api/admin/users/${id}/notification-settings`, { headers: adminHeaders() });
    return data;
  },
  async updateUserNotificationSettings(id: string, payload: UserNotificationSettingsUpdate): Promise<UserNotificationSettings> {
    const { data } = await apiClient.put<UserNotificationSettings>(`/api/admin/users/${id}/notification-settings`, payload, { headers: adminHeaders() });
    return data;
  },
  async resetUserPassword(id: string, newPassword: string): Promise<void> {
    await apiClient.post(`/api/admin/users/${id}/reset-password`, { new_password: newPassword }, { headers: adminHeaders() });
  },
  async changeUserRole(id: string, newRole: "seller" | "buyer"): Promise<void> {
    await apiClient.post(`/api/admin/users/${id}/change-role`, { new_role: newRole }, { headers: adminHeaders() });
  },
  async creditUserWallet(id: string, amountCredits: number, reason: string, idempotencyKey: string): Promise<{ balance_paise: number }> {
    const { data } = await apiClient.post(
      `/api/admin/users/${id}/credit-wallet`,
      { amount_credits: amountCredits, reason },
      { headers: { ...adminHeaders(), "Idempotency-Key": idempotencyKey } }
    );
    return data;
  },
  async updateUser(id: string, body: Record<string, any>): Promise<any> {
    const { data } = await apiClient.put(`/api/admin/users/${id}`, body, { headers: adminHeaders() });
    return data;
  },
  async updateDealerReviews(id: string, body: DealerReviewsData): Promise<DealerReviewsData> {
    const { data } = await apiClient.put(`/api/admin/users/${id}/reviews`, body, { headers: adminHeaders() });
    return data;
  },
  async bulkVerifyUsers(params: { user_ids?: string[]; role?: string }): Promise<{ verified_count: number; verified_ids: string[] }> {
    const { data } = await apiClient.post("/api/admin/users/bulk-verify", params, { headers: adminHeaders() });
    return data;
  },
  async bulkUnverifyUsers(userIds: string[]): Promise<{ unverified_count: number; unverified_ids: string[] }> {
    const { data } = await apiClient.post("/api/admin/users/bulk-unverify", { user_ids: userIds }, { headers: adminHeaders() });
    return data;
  },
  /** One-time repair for accounts showing "Never" under Last Login despite
   *  having actually signed in — backfills last_login_at from their most
   *  recent session (falls back to created_at). Safe to re-run; only touches
   *  users where the field is still missing. */
  async backfillLastLogin(): Promise<{ scanned: number; backfilled: number }> {
    const { data } = await apiClient.post("/api/admin/users/backfill-last-login", {}, { headers: adminHeaders() });
    return data;
  },
  /** One-time repair for accounts created before `signup_method` tracking
   *  existed — infers "otp" vs "google"/"apple" from auth_provider. Safe to
   *  re-run; only touches users where the field is still missing. */
  async backfillSignupMethod(): Promise<{ scanned: number; backfilled: number }> {
    const { data } = await apiClient.post("/api/admin/users/backfill-signup-method", {}, { headers: adminHeaders() });
    return data;
  },
  /** One-time BEST-EFFORT repair for accounts that logged in before
   *  `last_login_method` tracking existed — inferred from auth_provider /
   *  password-hash presence, not recovered fact. Safe to re-run; only
   *  touches users who have actually logged in and are still missing it. */
  async backfillLoginMethod(): Promise<{ scanned: number; backfilled: number }> {
    const { data } = await apiClient.post("/api/admin/users/backfill-login-method", {}, { headers: adminHeaders() });
    return data;
  },
  async deleteUser(id: string): Promise<void> {
    await apiClient.delete(`/api/admin/users/${id}`, { headers: adminHeaders() });
  },
  /** Undo a user's self-serve "Delete account" (account_disabled) — re-activates
   * the login and un-hides their deals/posts. Returns the refreshed user doc. */
  async restoreUserAccount(id: string): Promise<any> {
    const { data } = await apiClient.post(`/api/admin/users/${id}/restore-account`, {}, { headers: adminHeaders() });
    return data;
  },
  /** Read-only, 30-min "view as user" token (audited). */
  async impersonateUser(id: string): Promise<{ access_token: string; user: { id: string; email?: string; name?: string; role?: string } }> {
    const { data } = await apiClient.post(`/api/admin/users/${id}/impersonate`, {}, { headers: adminHeaders() });
    return data;
  },
  /** Password-set requests — an OTP-only account's FIRST password does not take
   *  effect on submit: POST /api/v1/auth/change-password parks the hash on the
   *  user doc as `password_set_request` and one of these decisions is what makes
   *  it real (approve) or discards it (reject, with a reason the user reads in
   *  Settings before resubmitting). See
   *  backend/app/modules/admin/password_request_routes.py.
   *
   *  These responses carry status/timestamps/reviewer metadata ONLY — the
   *  password and its hash are never sent to the admin panel. */
  async getPendingPasswordRequests(): Promise<PasswordRequestRow[]> {
    const { data } = await apiClient.get<PasswordRequestRow[]>("/api/admin/password-requests/pending", { headers: adminHeaders() });
    return data;
  },
  async getReviewedPasswordRequests(): Promise<PasswordRequestRow[]> {
    const { data } = await apiClient.get<PasswordRequestRow[]>("/api/admin/password-requests/reviewed", { headers: adminHeaders() });
    return data;
  },
  async approvePasswordRequest(userId: string): Promise<{ status: string; user_id: string }> {
    const { data } = await apiClient.post(`/api/admin/password-requests/${userId}/approve`, {}, { headers: adminHeaders() });
    return data;
  },
  async rejectPasswordRequest(userId: string, reason: string): Promise<{ status: string; user_id: string }> {
    const { data } = await apiClient.post(`/api/admin/password-requests/${userId}/reject`, { reason }, { headers: adminHeaders() });
    return data;
  },
  async verifyGstDetails(gstin: string): Promise<any> {
    const { data } = await apiClient.get("/api/admin/gst-verify", { params: { gstin }, headers: adminHeaders() });
    return data;
  },
  /** Verifies + PERSISTS a user's GST data directly from the "Onboarding
   *  Data" panel — unlike verifyGstDetails above (preview only) and unlike
   *  approveGstVerificationJob below (which needs an existing job_id), this
   *  works for a user with zero gst_verification_jobs rows: it live-verifies
   *  the GSTIN and upserts the result onto the user's profile/step data,
   *  creating (or reusing) a job row so it still shows up in the Jobs list. */
  async confirmGstVerification(payload: { user_id: string; gstin: string; step_key?: string | null }): Promise<any> {
    const { data } = await apiClient.post("/api/admin/gst-verify", payload, { headers: adminHeaders() });
    return data;
  },

  // GST verification jobs (async queue) — NOTE: these intentionally hit the
  // /api/v1 prefix, unlike every other admin endpoint in this file. See
  // backend/app/modules/admin/gst_verification_routes.py.
  async getGstVerificationJobs(status?: string): Promise<GstVerificationJobSummary[]> {
    const { data } = await apiClient.get<GstVerificationJobSummary[]>("/api/v1/admin/gst-verification-jobs", {
      params: status ? { status } : {}, headers: adminHeaders(),
    });
    return data;
  },
  async getGstVerificationJob(jobId: string): Promise<GstVerificationJobDetail> {
    const { data } = await apiClient.get<GstVerificationJobDetail>(`/api/v1/admin/gst-verification-jobs/${jobId}`, { headers: adminHeaders() });
    return data;
  },
  async stopGstVerificationJob(jobId: string): Promise<GstVerificationJobDetail> {
    const { data } = await apiClient.post<GstVerificationJobDetail>(`/api/v1/admin/gst-verification-jobs/${jobId}/stop`, {}, { headers: adminHeaders() });
    return data;
  },
  async approveGstVerificationJob(jobId: string): Promise<GstVerificationJobDetail> {
    const { data } = await apiClient.post<GstVerificationJobDetail>(`/api/v1/admin/gst-verification-jobs/${jobId}/approve`, {}, { headers: adminHeaders() });
    return data;
  },
  /** Live-fetches GST portal details for the job's GSTIN without persisting
   *  anything — powers the "Verify" popup preview before an admin commits. */
  async fetchGstVerificationJobDetails(jobId: string): Promise<GstVerificationDetails> {
    const { data } = await apiClient.post<GstVerificationDetails>(`/api/v1/admin/gst-verification-jobs/${jobId}/fetch-details`, {}, { headers: adminHeaders() });
    return data;
  },
  async rejectGstVerificationJob(jobId: string): Promise<GstVerificationJobDetail> {
    const { data } = await apiClient.post<GstVerificationJobDetail>(`/api/v1/admin/gst-verification-jobs/${jobId}/reject`, {}, { headers: adminHeaders() });
    return data;
  },

  // Deals
  async getDeals(params: Record<string, any> = {}): Promise<PaginatedResponse> {
    const { data } = await apiClient.get<PaginatedResponse>("/api/admin/deals", { params, headers: adminHeaders() });
    return data;
  },
  async getDeal(id: string): Promise<any> {
    const { data } = await apiClient.get(`/api/admin/deals/${id}`, { headers: adminHeaders() });
    return data;
  },
  async getStuckShadowDeals(params: Record<string, any> = {}): Promise<PaginatedResponse> {
    const { data } = await apiClient.get<PaginatedResponse>("/api/admin/deals/stuck-shadow", { params, headers: adminHeaders() });
    return data;
  },
  async getStuckShadowDeal(id: string): Promise<StuckShadowDealDetail> {
    const { data } = await apiClient.get<StuckShadowDealDetail>(`/api/admin/deals/stuck-shadow/${id}`, { headers: adminHeaders() });
    return data;
  },
  async updateDeal(id: string, body: Record<string, any>): Promise<any> {
    const { data } = await apiClient.put(`/api/admin/deals/${id}`, body, { headers: adminHeaders() });
    return data;
  },
  async deleteDeal(id: string): Promise<void> {
    await apiClient.delete(`/api/admin/deals/${id}`, { headers: adminHeaders() });
  },
  /** Take a live typed deal down (lifecycle.is_active -> false) — e.g. after
   *  the possible-duplicate check flags it. `collection` is the typed
   *  collection (deal_packages, deal_stays, ...), resolvable via
   *  dealsService.resolveCollection(dealId). */
  async unpublishDeal(collection: string, dealId: string): Promise<any> {
    const { data } = await apiClient.post(
      `/api/admin/deals/${encodeURIComponent(collection)}/${encodeURIComponent(dealId)}/unpublish`,
      {},
      { headers: adminHeaders() },
    );
    return data;
  },
  async publishDeal(collection: string, dealId: string, opts?: { addMissingMarket?: boolean }): Promise<any> {
    const { data } = await apiClient.post(
      `/api/admin/deals/${encodeURIComponent(collection)}/${encodeURIComponent(dealId)}/publish`,
      opts?.addMissingMarket ? { add_missing_market: true } : {},
      { headers: adminHeaders() },
    );
    return data;
  },
  /** Deals currently blocked on the last-minute-deal admin review gate
   *  (admin_review.status === "pending" across every typed collection). */
  async listPendingLastMinuteDeals(): Promise<any[]> {
    const { data } = await apiClient.get("/api/admin/last-minute-deals/pending", { headers: adminHeaders() });
    return data;
  },
  /** Clears the review gate. `publish=true` (default) also publishes the deal
   *  immediately; `publish=false` only approves — the seller still has to
   *  publish it themselves from My Inventory. */
  async approveLastMinuteDeal(collection: string, dealId: string, publish: boolean): Promise<any> {
    const { data } = await apiClient.post(
      `/api/admin/last-minute-deals/${encodeURIComponent(collection)}/${encodeURIComponent(dealId)}/approve`,
      { publish },
      { headers: adminHeaders() },
    );
    return data;
  },
  /** Rejects a pending last-minute deal — keeps it offline, records the reason. */
  async rejectLastMinuteDeal(collection: string, dealId: string, reason: string): Promise<any> {
    const { data } = await apiClient.post(
      `/api/admin/last-minute-deals/${encodeURIComponent(collection)}/${encodeURIComponent(dealId)}/reject`,
      { reason },
      { headers: adminHeaders() },
    );
    return data;
  },
  /** Full decision history — every last-minute deal an admin has approved or
   *  rejected, newest first, with the reviewer's name resolved. */
  async listReviewedLastMinuteDeals(): Promise<any[]> {
    const { data } = await apiClient.get("/api/admin/last-minute-deals/reviewed", { headers: adminHeaders() });
    return data;
  },
  /** Deals currently blocked because their entire extracted travel-date
   *  window has already passed (admin_review.flagged_reason === "dates_expired"). */
  async listPendingDateExpiryDeals(): Promise<any[]> {
    const { data } = await apiClient.get("/api/admin/date-expiry-deals/pending", { headers: adminHeaders() });
    return data;
  },
  /** Clears the review gate, optionally correcting the travel dates first via
   *  `validity` (same shape the deal's own Valid From/Until edit form uses).
   *  `publish=true` (default) also publishes immediately. */
  async approveDateExpiryDeal(
    collection: string, dealId: string, publish: boolean, validity?: Record<string, any>,
  ): Promise<any> {
    const { data } = await apiClient.post(
      `/api/admin/date-expiry-deals/${encodeURIComponent(collection)}/${encodeURIComponent(dealId)}/approve`,
      { publish, ...(validity ? { validity } : {}) },
      { headers: adminHeaders() },
    );
    return data;
  },
  /** Rejects a pending date-expiry deal — keeps it offline, records the reason. */
  async rejectDateExpiryDeal(collection: string, dealId: string, reason: string): Promise<any> {
    const { data } = await apiClient.post(
      `/api/admin/date-expiry-deals/${encodeURIComponent(collection)}/${encodeURIComponent(dealId)}/reject`,
      { reason },
      { headers: adminHeaders() },
    );
    return data;
  },
  /** Deals currently blocked because their category/subcategory/tags match a
   *  "cancelled" pattern (admin_review.flagged_reason === "cancelled"). */
  async listPendingCancelledDeals(): Promise<any[]> {
    const { data } = await apiClient.get("/api/admin/cancelled-deals/pending", { headers: adminHeaders() });
    return data;
  },
  /** Clears the review gate. `publish=true` (default) also publishes the deal
   *  immediately; `publish=false` only approves — the seller still has to
   *  publish it themselves from My Inventory. */
  async approveCancelledDeal(collection: string, dealId: string, publish: boolean): Promise<any> {
    const { data } = await apiClient.post(
      `/api/admin/cancelled-deals/${encodeURIComponent(collection)}/${encodeURIComponent(dealId)}/approve`,
      { publish },
      { headers: adminHeaders() },
    );
    return data;
  },
  /** Rejects a pending cancelled deal — keeps it offline, records the reason. */
  async rejectCancelledDeal(collection: string, dealId: string, reason: string): Promise<any> {
    const { data } = await apiClient.post(
      `/api/admin/cancelled-deals/${encodeURIComponent(collection)}/${encodeURIComponent(dealId)}/reject`,
      { reason },
      { headers: adminHeaders() },
    );
    return data;
  },
  /** Full decision history — every cancelled deal an admin has approved or
   *  rejected, newest first, with the reviewer's name resolved. */
  async listReviewedCancelledDeals(): Promise<any[]> {
    const { data } = await apiClient.get("/api/admin/cancelled-deals/reviewed", { headers: adminHeaders() });
    return data;
  },
  /** Deals hard-deleted mid AI-analysis-pipeline (analysis failure,
   *  seller_type_mismatch, or a stale job getting reaped) — newest first,
   *  with the full deal snapshot as it stood at failure time. */
  async listStuckDeals(params: Record<string, any> = {}): Promise<PaginatedResponse> {
    const { data } = await apiClient.get<PaginatedResponse>("/api/admin/stuck-deals", { params, headers: adminHeaders() });
    return data;
  },
  async getStuckDeal(recordId: string): Promise<any> {
    const { data } = await apiClient.get(`/api/admin/stuck-deals/${encodeURIComponent(recordId)}`, { headers: adminHeaders() });
    return data;
  },
  /** Permanently removes a stuck-deal record. The underlying deal is already
   *  gone from live collections by this point — this only clears the record. */
  async deleteStuckDeal(recordId: string): Promise<void> {
    await apiClient.delete(`/api/admin/stuck-deals/${encodeURIComponent(recordId)}`, { headers: adminHeaders() });
  },
  /** Pinecone-status diagnostic for the deal-detail "Pinecone" tab — indexed
   *  chunks, namespace, embedding model/dimension, stored metadata + live
   *  similarity score per chunk. `collection` is the typed collection
   *  (deal_packages, deal_stays, ...), same as publish/unpublish above. */
  async getDealPineconeStatus(collection: string, dealId: string): Promise<DealPineconeStatus> {
    const { data } = await apiClient.get<DealPineconeStatus>(
      `/api/admin/deals/${encodeURIComponent(collection)}/${encodeURIComponent(dealId)}/pinecone`,
      { headers: adminHeaders() },
    );
    return data;
  },
  /** Force a synchronous re-index into Pinecone — same path publish/update fires
   *  in the background, run on demand from the Pinecone tab's "Reindex now" button. */
  async reindexDealPinecone(collection: string, dealId: string): Promise<{ success: boolean; namespace: string }> {
    const { data } = await apiClient.post(
      `/api/admin/deals/${encodeURIComponent(collection)}/${encodeURIComponent(dealId)}/pinecone/reindex`,
      {},
      { headers: adminHeaders() },
    );
    return data;
  },

  // RAG / Pinecone indexing — sidebar "Indexing" page.
  /** Which Pinecone index/embed model this deployment is actually pointed at —
   *  env-driven, easy to lose track of across dev/staging/prod. */
  async getRagIndexingConfig(): Promise<RagIndexingConfig> {
    const { data } = await apiClient.get<RagIndexingConfig>("/api/admin/rag-indexing/config", { headers: adminHeaders() });
    return data;
  },
  /** Every deal (across all indexed collections) whose last index attempt failed,
   *  newest first, with the real error text + a coarse category. */
  async listFailedRagDeals(params: Record<string, any> = {}): Promise<PaginatedResponse<RagFailedDeal>> {
    const { data } = await apiClient.get<PaginatedResponse<RagFailedDeal>>(
      "/api/admin/rag-indexing/failed",
      { params, headers: adminHeaders() },
    );
    return data;
  },
  /** Read-only — whatever the scheduled 24h job found last time it ran the full
   *  live Mongo-vs-Pinecone diff. NOT a live scan: there's no admin-triggered
   *  trigger for that check anymore (it's the expensive path, kept exclusively
   *  on the schedule). `scanned_at` is null if that job hasn't run yet. */
  async getMissingRagScan(): Promise<RagMissingScanResponse> {
    const { data } = await apiClient.get("/api/admin/rag-indexing/missing", { headers: adminHeaders() });
    return data;
  },
  /** Read-only status of whichever scheduled job last ran: the 30-min
   *  flagged-only auto-retry or the 24h full diff. Nothing on this page
   *  triggers either manually anymore — both run entirely on their own
   *  schedule (app.main._rag_flagged_retry_loop / _rag_reconcile_loop). */
  async getRagReindexAllStatus(): Promise<RagReindexAllStatus> {
    const { data } = await apiClient.get<RagReindexAllStatus>("/api/admin/rag-indexing/reindex-all/status", { headers: adminHeaders() });
    return data;
  },

  // Home Deal Banners
  async getHomeDealBanners(params: Record<string, any> = {}): Promise<any[]> {
    const { data } = await apiClient.get<any[]>("/api/admin/home-deal-banners", { params, headers: adminHeaders() });
    return data;
  },
  async createHomeDealBanner(formData: FormData): Promise<any> {
    const { data } = await apiClient.post("/api/admin/home-deal-banners", formData, {
      headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" },
    });
    return data;
  },
  async updateHomeDealBanner(id: string, formData: FormData): Promise<any> {
    const { data } = await apiClient.put(`/api/admin/home-deal-banners/${id}`, formData, {
      headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" },
    });
    return data;
  },
  async deleteHomeDealBanner(id: string): Promise<void> {
    await apiClient.delete(`/api/admin/home-deal-banners/${id}`, { headers: adminHeaders() });
  },
  async searchHomeDealBannerSellers(params: { search?: string; role?: string } = {}): Promise<any[]> {
    const { data } = await apiClient.get<any[]>("/api/admin/home-deal-banners/sellers", { params, headers: adminHeaders() });
    return data;
  },
  async searchHomeDealBannerSellerDeals(sellerId: string, params: { search?: string } = {}): Promise<any[]> {
    const { data } = await apiClient.get<any[]>(`/api/admin/home-deal-banners/sellers/${sellerId}/deals`, { params, headers: adminHeaders() });
    return data;
  },

  // Analytics
  async getAnalytics(days = 30): Promise<AnalyticsSummary> {
    const { data } = await apiClient.get<AnalyticsSummary>("/api/admin/analytics", { params: { days }, headers: adminHeaders() });
    return data;
  },

  // AI Media Generations (flyers + itineraries): who generated, tokens, cost ($/₹)
  async getAiGenerations(params: {
    asset_type?: "all" | "flyer" | "itinerary" | "video";
    page?: number;
    page_size?: number;
    search?: string;
    include_records?: boolean;
    date_from?: string;
    date_to?: string;
  } = {}): Promise<AiGenerationsResponse> {
    const { data } = await apiClient.get<AiGenerationsResponse>("/api/admin/ai-generations", { params, headers: adminHeaders() });
    return data;
  },

  // Auto-generated create-deal flyers: source asset, generated flyer, tokens, cost ($/₹)
  async getDealFlyerGenerations(params: {
    page?: number;
    page_size?: number;
    search?: string;
    include_records?: boolean;
    date_from?: string;
    date_to?: string;
  } = {}): Promise<DealFlyerGenerationsResponse> {
    const { data } = await apiClient.get<DealFlyerGenerationsResponse>(
      "/api/admin/ai-generations/deal-flyers",
      { params, headers: adminHeaders() },
    );
    return data;
  },

  // AI Search usage (RAG chat + Market research): per-user query count, tokens, cost ($/₹) by model
  async getRagChatUsage(params: {
    page?: number;
    page_size?: number;
    search?: string;
    date_from?: string;
    date_to?: string;
  } = {}): Promise<AiSearchUsageResponse> {
    const { data } = await apiClient.get<AiSearchUsageResponse>("/api/admin/ai-search/rag-chat-usage", { params, headers: adminHeaders() });
    return data;
  },
  async getMarketResearchUsage(params: {
    page?: number;
    page_size?: number;
    search?: string;
    date_from?: string;
    date_to?: string;
  } = {}): Promise<AiSearchUsageResponse> {
    const { data } = await apiClient.get<AiSearchUsageResponse>("/api/admin/ai-search/market-research-usage", { params, headers: adminHeaders() });
    return data;
  },

  // Market Pulse: demand/geo/price signal from itinerary/flyer/video/RAG-chat generations
  async getMarketPulseFilterOptions(params: {
    continent?: string; subregion?: string; country_iso2?: string;
  } = {}): Promise<MarketPulseFilterOptions> {
    const { data } = await apiClient.get<MarketPulseFilterOptions>("/api/admin/market-pulse/filter-options", { params, headers: adminHeaders() });
    return data;
  },
  async getMarketPulseSummary(params: MarketPulseFilters = {}): Promise<MarketPulseSummary> {
    const { data } = await apiClient.get<MarketPulseSummary>("/api/admin/market-pulse/summary", { params, headers: adminHeaders() });
    return data;
  },
  async getMarketPulseTable(params: MarketPulseFilters & { page?: number; page_size?: number } = {}): Promise<MarketPulseTableResponse> {
    const { data } = await apiClient.get<MarketPulseTableResponse>("/api/admin/market-pulse/table", { params, headers: adminHeaders() });
    return data;
  },
  async getMarketPulseGeoRollup(
    params: MarketPulseFilters & { level: GeoRollupLevel },
  ): Promise<GeoRollupResponse> {
    const { data } = await apiClient.get<GeoRollupResponse>("/api/admin/market-pulse/geo-rollup", { params, headers: adminHeaders() });
    return data;
  },
  async getMarketPulsePriceTrend(params: PriceTrendFilters = {}): Promise<PriceTrendResponse> {
    const { data } = await apiClient.get<PriceTrendResponse>("/api/admin/market-pulse/price-trend", { params, headers: adminHeaders() });
    return data;
  },
  /** Fetches the .xlsx as a blob (the export endpoint needs the admin auth header,
   *  which a plain <a href> can't attach) and triggers a browser save. */
  async exportMarketPulseExcel(params: MarketPulseFilters = {}): Promise<void> {
    const response = await apiClient.get("/api/admin/market-pulse/table/export", {
      params, headers: adminHeaders(), responseType: "blob",
    });
    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "market_pulse_export.xlsx";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },

  // Community / Polls
  async getCommunityPosts(params: Record<string, any> = {}): Promise<PaginatedResponse> {
    const { data } = await apiClient.get<PaginatedResponse>("/api/admin/community", { params, headers: adminHeaders() });
    return data;
  },
  async createCommunityPost(body: Record<string, any>): Promise<any> {
    const { data } = await apiClient.post("/api/admin/community", body, { headers: adminHeaders() });
    return data;
  },
  async previewCommunityAudience(body: Record<string, any>): Promise<CommunityAudiencePreview> {
    const { data } = await apiClient.post<CommunityAudiencePreview>(
      "/api/community/admin/audience-preview",
      body,
      { headers: adminHeaders() },
    );
    return data;
  },
  async uploadCommunityMedia(file: File): Promise<{ key: string; url: string; expires_in: number }> {
    const formData = new FormData();
    formData.append("file", file);
    const { data } = await apiClient.post<{ key: string; url: string; expires_in: number }>(
      "/api/admin/storage/upload-community-media",
      formData,
      { headers: adminHeaders() },
    );
    return data;
  },
  async updateCommunityPost(id: string, body: Record<string, any>): Promise<any> {
    const { data } = await apiClient.put(`/api/admin/community/${id}`, body, { headers: adminHeaders() });
    return data;
  },
  async deleteCommunityPost(id: string): Promise<void> {
    await apiClient.delete(`/api/admin/community/${id}`, { headers: adminHeaders() });
  },
  async getCommunityEngagement(id: string): Promise<CommunityEngagement> {
    const { data } = await apiClient.get<CommunityEngagement>(
      `/api/community/posts/${id}/engagement`,
      { headers: adminHeaders() },
    );
    return data;
  },

  // Webinars
  async getWebinars(params: Record<string, any> = {}): Promise<PaginatedResponse> {
    const { data } = await apiClient.get<PaginatedResponse>("/api/admin/webinars", { params, headers: adminHeaders() });
    return data;
  },
  async createWebinar(body: Record<string, any>): Promise<any> {
    const { data } = await apiClient.post("/api/admin/webinars", body, { headers: adminHeaders() });
    return data;
  },
  async updateWebinar(id: string, body: Record<string, any>): Promise<any> {
    const { data } = await apiClient.put(`/api/admin/webinars/${id}`, body, { headers: adminHeaders() });
    return data;
  },
  async deleteWebinar(id: string): Promise<void> {
    await apiClient.delete(`/api/admin/webinars/${id}`, { headers: adminHeaders() });
  },
  async uploadWebinarFile(id: string, file: File): Promise<{ url: string; file_type: string }> {
    const formData = new FormData();
    formData.append("file", file);
    const { data } = await apiClient.post(`/api/admin/webinars/${id}/upload`, formData, {
      headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" },
    });
    return data;
  },

  // News
  async getNews(params: Record<string, any> = {}): Promise<PaginatedResponse> {
    const { data } = await apiClient.get<PaginatedResponse>("/api/admin/news", { params, headers: adminHeaders() });
    return data;
  },
  async createNews(body: Record<string, any>): Promise<any> {
    const { data } = await apiClient.post("/api/admin/news", body, { headers: adminHeaders() });
    return data;
  },
  /** Full-featured create (cover image, gallery, attachments) — admin auth via require_seller's super_admin allowance. */
  async createNewsWithFiles(formData: FormData): Promise<any> {
    const { data } = await apiClient.post("/api/news/submit", formData, {
      headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" },
    });
    return data;
  },
  async updateNews(id: string, body: Record<string, any>): Promise<any> {
    const { data } = await apiClient.put(`/api/admin/news/${id}`, body, { headers: adminHeaders() });
    return data;
  },
  async getNewsArticle(id: string): Promise<any> {
    const { data } = await apiClient.get(`/api/admin/news/${id}`, { headers: adminHeaders() });
    return data;
  },
  /** Full edit (text fields + cover/gallery/attachment upload & removal) — any article, not just admin-authored. */
  async updateNewsWithFiles(id: string, formData: FormData): Promise<any> {
    const { data } = await apiClient.put(`/api/admin/news/${id}/full`, formData, {
      headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" },
    });
    return data;
  },
  async deleteNews(id: string): Promise<void> {
    await apiClient.delete(`/api/admin/news/${id}`, { headers: adminHeaders() });
  },
  /** Re-checks already-ingested RSS articles' cover images, upgrading/clearing
   *  any that would look blurry when upscaled into a card/hero box, and
   *  clearing stale external cover_image_url pointers left alongside a good
   *  self-hosted copy (`repaired`).
   *
   *  `force` re-verifies every RSS article rather than only ones in a
   *  known-suspect state — slower, but it can never come back as a no-op. */
  async backfillNewsCoverImages(
    limit = 500,
    force = false
  ): Promise<{ checked: number; upgraded: number; repaired: number; cleared: number; unchanged: number; failed: number }> {
    const { data } = await apiClient.post("/api/news/backfill-images", null, {
      params: { limit, force },
      headers: adminHeaders(),
    });
    return data;
  },

  // Contacts
  async getContacts(params: Record<string, any> = {}): Promise<PaginatedResponse> {
    const { data } = await apiClient.get<PaginatedResponse>("/api/admin/contacts", { params, headers: adminHeaders() });
    return data;
  },
  async deleteContact(id: string): Promise<void> {
    await apiClient.delete(`/api/admin/contacts/${id}`, { headers: adminHeaders() });
  },

  // Leads / Enquiries
  async getLeads(params: Record<string, any> = {}): Promise<PaginatedResponse> {
    const { data } = await apiClient.get<PaginatedResponse>("/api/admin/leads", { params, headers: adminHeaders() });
    return data;
  },
  async getItineraryDrafts(params: Record<string, any> = {}): Promise<PaginatedResponse> {
    const { data } = await apiClient.get<PaginatedResponse>("/api/admin/leads/itinerary-drafts", { params, headers: adminHeaders() });
    return data;
  },

  // WebSocket connection health
  async getWebsocketConnections(): Promise<WebsocketConnectionsResponse> {
    const { data } = await apiClient.get<WebsocketConnectionsResponse>("/api/admin/websocket-connections", { headers: adminHeaders() });
    return data;
  },

  async getRedisPoolSize(): Promise<RedisPoolSizeResponse> {
    const { data } = await apiClient.get<RedisPoolSizeResponse>("/api/admin/redis-pool-size", { headers: adminHeaders() });
    return data;
  },

  async setRedisPoolSize(size: number): Promise<RedisPoolSizeResponse> {
    const { data } = await apiClient.put<RedisPoolSizeResponse>("/api/admin/redis-pool-size", { size }, { headers: adminHeaders() });
    return data;
  },

  async getAllRedisPoolSizes(): Promise<AllRedisPoolSizesResponse> {
    const { data } = await apiClient.get<AllRedisPoolSizesResponse>("/api/admin/redis-pool-sizes", { headers: adminHeaders() });
    return data;
  },

  async getRealtimeOverview(): Promise<RealtimeOverviewResponse> {
    const { data } = await apiClient.get<RealtimeOverviewResponse>("/api/admin/realtime/overview", { headers: adminHeaders() });
    return data;
  },

  async getRedisOverview(): Promise<RedisOverviewResponse> {
    const { data } = await apiClient.get<RedisOverviewResponse>("/api/admin/redis/overview", { headers: adminHeaders() });
    return data;
  },

  async getRedisNamespaces(match = "*"): Promise<RedisNamespacesResponse> {
    const { data } = await apiClient.get<RedisNamespacesResponse>(
      `/api/admin/redis/namespaces?match=${encodeURIComponent(match)}`,
      { headers: adminHeaders() },
    );
    return data;
  },

  async getRedisClients(): Promise<RedisClientsResponse> {
    const { data } = await apiClient.get<RedisClientsResponse>("/api/admin/redis/clients", { headers: adminHeaders() });
    return data;
  },

  async getRedisQueues(): Promise<RedisQueuesResponse> {
    const { data } = await apiClient.get<RedisQueuesResponse>("/api/admin/redis/queues", { headers: adminHeaders() });
    return data;
  },


  async setRedisPoolSizeFor(service: string, size: number): Promise<{ service: string; current: number }> {
    const { data } = await apiClient.put(`/api/admin/redis-pool-sizes/${encodeURIComponent(service)}`, { size }, { headers: adminHeaders() });
    return data;
  },

  async setRedisMaxclients(value: number): Promise<RedisMaxclientsResponse> {
    const { data } = await apiClient.put<RedisMaxclientsResponse>("/api/admin/redis-maxclients", { value }, { headers: adminHeaders() });
    return data;
  },

  // Rec engine health — its own dedicated Redis, its own sockets, its own jobs
  async getRecRedisConnections(): Promise<RecRedisConnectionsResponse> {
    const { data } = await apiClient.get<RecRedisConnectionsResponse>("/api/admin/rec-engine/connections", { headers: adminHeaders() });
    return data;
  },

  async getRecWsHealth(): Promise<RecWsHealthResponse> {
    const { data } = await apiClient.get<RecWsHealthResponse>("/api/admin/rec-engine/ws-health", { headers: adminHeaders() });
    return data;
  },

  async getRecJobHealth(): Promise<RecJobHealthResponse> {
    const { data } = await apiClient.get<RecJobHealthResponse>("/api/admin/rec-engine/job-health", { headers: adminHeaders() });
    return data;
  },

  async getRecRedisOverview(): Promise<RecRedisOverviewResponse> {
    const { data } = await apiClient.get<RecRedisOverviewResponse>("/api/admin/rec-engine/redis/overview", { headers: adminHeaders() });
    return data;
  },

  async getRecRedisNamespaces(match = "*"): Promise<RedisNamespacesResponse> {
    const { data } = await apiClient.get<RedisNamespacesResponse>(
      `/api/admin/rec-engine/redis/namespaces?match=${encodeURIComponent(match)}`,
      { headers: adminHeaders() },
    );
    return data;
  },

  async getRecRedisClients(): Promise<RedisClientsResponse> {
    const { data } = await apiClient.get<RedisClientsResponse>("/api/admin/rec-engine/redis/clients", { headers: adminHeaders() });
    return data;
  },

  async getRecFallbackHealth(): Promise<RecFallbackHealthResponse> {
    const { data } = await apiClient.get<RecFallbackHealthResponse>("/api/admin/rec-engine/redis/fallback-health", { headers: adminHeaders() });
    return data;
  },

  // CDN / storage-migration queue's own dedicated Redis (CDN_REDIS_URL) —
  // never the shared backend/RAG/OCR Redis, never rec_engine's instance.
  async getCdnRedisOverview(): Promise<CdnRedisOverviewResponse> {
    const { data } = await apiClient.get<CdnRedisOverviewResponse>("/api/admin/cdn-redis/overview", { headers: adminHeaders() });
    return data;
  },

  async getCdnRedisNamespaces(match = "storage_migration:*"): Promise<RedisNamespacesResponse> {
    const { data } = await apiClient.get<RedisNamespacesResponse>(
      `/api/admin/cdn-redis/namespaces?match=${encodeURIComponent(match)}`,
      { headers: adminHeaders() },
    );
    return data;
  },

  async getCdnRedisClients(): Promise<RedisClientsResponse> {
    const { data } = await apiClient.get<RedisClientsResponse>("/api/admin/cdn-redis/clients", { headers: adminHeaders() });
    return data;
  },

  async getCdnQueueStatus(): Promise<CdnQueueStatusResponse> {
    const { data } = await apiClient.get<CdnQueueStatusResponse>("/api/admin/cdn-redis/queue", { headers: adminHeaders() });
    return data;
  },

  // Activity
  async getRecentLogins(params: ActivityLoginsParams = {}): Promise<ActivityLoginsResponse> {
    const { data } = await apiClient.get("/api/admin/activity/recent-logins", { params, headers: adminHeaders() });
    return data;
  },
  async getLoginTrend(days = 30): Promise<{ trend: any[] }> {
    const { data } = await apiClient.get("/api/admin/activity/login-trend", { params: { days }, headers: adminHeaders() });
    return data;
  },
  async getRoleBreakdown(): Promise<{ breakdown: any[] }> {
    const { data } = await apiClient.get("/api/admin/activity/role-breakdown", { headers: adminHeaders() });
    return data;
  },
  async getRecentEvents(params: ActivityEventsParams = {}): Promise<ActivityEventsResponse> {
    const { data } = await apiClient.get("/api/admin/activity/recent-events", { params, headers: adminHeaders() });
    return data;
  },

  // Broadcast notifications
  async previewBroadcast(payload: BroadcastRequest): Promise<BroadcastPreview> {
    const { data } = await apiClient.post<BroadcastPreview>("/api/admin/notifications/broadcast/preview", payload, { headers: adminHeaders() });
    return data;
  },
  async sendBroadcast(payload: BroadcastRequest): Promise<Broadcast> {
    const { data } = await apiClient.post<Broadcast>("/api/admin/notifications/broadcast", payload, { headers: adminHeaders() });
    return data;
  },
  async getBroadcasts(params: Record<string, any> = {}): Promise<PaginatedResponse<Broadcast>> {
    const { data } = await apiClient.get<PaginatedResponse<Broadcast>>("/api/admin/notifications/broadcasts", { params, headers: adminHeaders() });
    return data;
  },
  async deleteBroadcast(id: string): Promise<void> {
    await apiClient.delete(`/api/admin/notifications/broadcasts/${id}`, { headers: adminHeaders() });
  },
  async patchBroadcast(id: string, payload: Partial<{ title: string; body: string; deep_link: string | null; scheduled_at: string; delivery_mode: string }>): Promise<Broadcast> {
    const { data } = await apiClient.patch<Broadcast>(`/api/admin/notifications/broadcasts/${id}`, payload, { headers: adminHeaders() });
    return data;
  },
  // ── Notification ops (FCM + in-app delivery observability) ────────────────
  async getNotifOpsHealth(): Promise<NotifOpsHealth> {
    const { data } = await apiClient.get<NotifOpsHealth>("/api/admin/notification-ops/health", { headers: adminHeaders() });
    return data;
  },
  async getNotifOpsDevices(params: Record<string, any> = {}): Promise<{ total: number; limit: number; offset: number; cap_per_user: number; items: NotifOpsDeviceOwner[] }> {
    const { data } = await apiClient.get("/api/admin/notification-ops/devices", { params, headers: adminHeaders() });
    return data;
  },
  async getNotifOpsUserDevices(userId: string, includeInactive = true): Promise<NotifOpsUserDevices> {
    const { data } = await apiClient.get<NotifOpsUserDevices>(`/api/admin/notification-ops/devices/${userId}`, {
      params: { include_inactive: includeInactive }, headers: adminHeaders(),
    });
    return data;
  },
  async pruneNotifOpsUserDevices(userId: string): Promise<{ user_id: string; before: number; deactivated: number; after: number }> {
    const { data } = await apiClient.post(`/api/admin/notification-ops/devices/${userId}/prune`, {}, { headers: adminHeaders() });
    return data;
  },
  async getNotifOpsDeliveries(params: Record<string, any> = {}): Promise<NotifOpsRange<NotifOpsDelivery>> {
    const { data } = await apiClient.get("/api/admin/notification-ops/deliveries", { params, headers: adminHeaders() });
    return data;
  },
  async getNotifOpsDaily(params: Record<string, any> = {}): Promise<NotifOpsRange<NotifOpsDailyRow>> {
    const { data } = await apiClient.get("/api/admin/notification-ops/daily", { params, headers: adminHeaders() });
    return data;
  },
  async getNotifOpsBroadcastRecipients(broadcastId: string, params: Record<string, any> = {}): Promise<NotifOpsBroadcastRecipients> {
    const { data } = await apiClient.get<NotifOpsBroadcastRecipients>(
      `/api/admin/notification-ops/broadcasts/${broadcastId}/recipients`,
      { params, headers: adminHeaders() },
    );
    return data;
  },
  async getNotifOpsDuplicates(params: Record<string, any> = {}): Promise<NotifOpsRange<NotifOpsDuplicate>> {
    const { data } = await apiClient.get("/api/admin/notification-ops/duplicates", { params, headers: adminHeaders() });
    return data;
  },
  async activateAllTokens(userId: string): Promise<{ activated_count: number }> {
    const { data } = await apiClient.post<{ activated_count: number }>(
      `/api/admin/users/${userId}/device-tokens/activate-all`,
      {},
      { headers: adminHeaders() },
    );
    return data;
  },

  // System
  async getSystemHealth(): Promise<SystemHealth> {
    const { data } = await apiClient.get<SystemHealth>("/api/admin/system/health", { headers: adminHeaders() });
    return data;
  },
  async getCollectionStats(): Promise<CollectionStat[]> {
    const { data } = await apiClient.get<CollectionStat[]>("/api/admin/system/collections", { headers: adminHeaders() });
    return data;
  },

  async getRagRetention(): Promise<{
    session_days: number;
    batch_size: number;
    updated_by: string | null;
    updated_at: string | null;
  }> {
    const { data } = await apiClient.get<{
      session_days: number;
      batch_size: number;
      updated_by: string | null;
      updated_at: string | null;
    }>("/api/admin/platform-config/rag-retention", { headers: adminHeaders() });
    return data;
  },

  async updateRagRetention(body: {
    session_days: number;
    batch_size: number;
  }): Promise<{ session_days: number; batch_size: number; updated_by: string | null; updated_at: string | null }> {
    const { data } = await apiClient.put("/api/admin/platform-config/rag-retention", body, { headers: adminHeaders() });
    return data;
  },

  // ── Inactive-user WhatsApp nudge ──────────────────────────────────────────
  async getInactivityNudgeConfig(): Promise<InactivityNudgeConfig> {
    const { data } = await apiClient.get<InactivityNudgeConfig>(
      "/api/admin/platform-config/inactivity-nudge", { headers: adminHeaders() });
    return data;
  },
  async updateInactivityNudgeConfig(body: Partial<InactivityNudgeConfig>): Promise<InactivityNudgeConfig> {
    const { data } = await apiClient.put(
      "/api/admin/platform-config/inactivity-nudge", body, { headers: adminHeaders() });
    return data;
  },
  async getInactivityNudgePreview(limit = 200): Promise<InactivityNudgePreview> {
    const { data } = await apiClient.get<InactivityNudgePreview>(
      "/api/admin/inactivity-nudge/preview", { params: { limit }, headers: adminHeaders() });
    return data;
  },
  async getInactivityNudgeRuns(limit = 20): Promise<{ runs: InactivityNudgeRun[] }> {
    const { data } = await apiClient.get<{ runs: InactivityNudgeRun[] }>(
      "/api/admin/inactivity-nudge/runs", { params: { limit }, headers: adminHeaders() });
    return data;
  },
  async runInactivityNudgeNow(): Promise<Record<string, unknown>> {
    const { data } = await apiClient.post(
      "/api/admin/inactivity-nudge/run-now", {}, { headers: adminHeaders() });
    return data;
  },

  /** On-demand S3 cross-account backup sync -- a supplement to the always-on
   *  S3 Replication rule (configured at the bucket level, outside the app).
   *  This just lets an admin force an extra sync right now (e.g. before a
   *  risky deploy) and see the last run's result. */
  async getS3BackupStatus(): Promise<S3BackupStatus> {
    const { data } = await apiClient.get<S3BackupStatus>("/api/admin/backup/s3/status", { headers: adminHeaders() });
    return data;
  },
  async triggerS3Backup(): Promise<{ message: string; status: S3BackupStatus }> {
    const { data } = await apiClient.post("/api/admin/backup/s3/trigger", {}, { headers: adminHeaders() });
    return data;
  },

  async getGstVerification(): Promise<{ enabled: boolean; updated_by: string | null; updated_at: string | null }> {
    const { data } = await apiClient.get<{ enabled: boolean; updated_by: string | null; updated_at: string | null }>(
      "/api/admin/platform-config/gst-verification",
      { headers: adminHeaders() }
    );
    return data;
  },

  async updateGstVerification(enabled: boolean): Promise<{ enabled: boolean; updated_by: string | null; updated_at: string | null }> {
    const { data } = await apiClient.put(
      "/api/admin/platform-config/gst-verification",
      { enabled },
      { headers: adminHeaders() }
    );
    return data;
  },

  // Separate from Verification Gating — only governs whether declining
  // (No / X) the deal AI chat confirm dialog still silently sends the
  // enquiry to the seller. Yes is unaffected and always goes through the
  // Enquiries verification gate instead. See deal_ai_chat_finalize in
  // backend/app/modules/dm/routes.py.
  async getSilentEnquiryOnDecline(): Promise<{ enabled: boolean; updated_by: string | null; updated_at: string | null }> {
    const { data } = await apiClient.get<{ enabled: boolean; updated_by: string | null; updated_at: string | null }>(
      "/api/admin/platform-config/enquiry-decline-behavior",
      { headers: adminHeaders() }
    );
    return data;
  },

  async updateSilentEnquiryOnDecline(enabled: boolean): Promise<{ enabled: boolean; updated_by: string | null; updated_at: string | null }> {
    const { data } = await apiClient.put(
      "/api/admin/platform-config/enquiry-decline-behavior",
      { enabled },
      { headers: adminHeaders() }
    );
    return data;
  },

  async getVerificationEmail(): Promise<{ enabled: boolean; updated_by: string | null; updated_at: string | null }> {
    const { data } = await apiClient.get<{ enabled: boolean; updated_by: string | null; updated_at: string | null }>(
      "/api/admin/platform-config/verification-email",
      { headers: adminHeaders() }
    );
    return data;
  },

  async updateVerificationEmail(enabled: boolean): Promise<{ enabled: boolean; updated_by: string | null; updated_at: string | null }> {
    const { data } = await apiClient.put(
      "/api/admin/platform-config/verification-email",
      { enabled },
      { headers: adminHeaders() }
    );
    return data;
  },

  async getTransactionalEmail(): Promise<{ enabled: boolean; updated_by: string | null; updated_at: string | null }> {
    const { data } = await apiClient.get<{ enabled: boolean; updated_by: string | null; updated_at: string | null }>(
      "/api/admin/platform-config/transactional-email",
      { headers: adminHeaders() }
    );
    return data;
  },

  async updateTransactionalEmail(enabled: boolean): Promise<{ enabled: boolean; updated_by: string | null; updated_at: string | null }> {
    const { data } = await apiClient.put(
      "/api/admin/platform-config/transactional-email",
      { enabled },
      { headers: adminHeaders() }
    );
    return data;
  },

  // Automatic deletion of uploads nobody used (Flyers / Videos / Itinerary / Ask Deal).
  // false pauses it: unused files then stay in storage until it is switched back on.
  async getUploadCleanup(): Promise<{ enabled: boolean; updated_by: string | null; updated_at: string | null }> {
    const { data } = await apiClient.get<{ enabled: boolean; updated_by: string | null; updated_at: string | null }>(
      "/api/admin/platform-config/upload-cleanup",
      { headers: adminHeaders() }
    );
    return data;
  },

  async updateUploadCleanup(enabled: boolean): Promise<{ enabled: boolean; updated_by: string | null; updated_at: string | null }> {
    const { data } = await apiClient.put(
      "/api/admin/platform-config/upload-cleanup",
      { enabled },
      { headers: adminHeaders() }
    );
    return data;
  },

  // AI reading of files attached in Ask Deal (a paid vision-model call). false: no file is read and
  // no model call is made; the user's message still goes through without the file.
  async getAskFileReading(): Promise<{ enabled: boolean; updated_by: string | null; updated_at: string | null }> {
    const { data } = await apiClient.get<{ enabled: boolean; updated_by: string | null; updated_at: string | null }>(
      "/api/admin/platform-config/ask-file-reading",
      { headers: adminHeaders() }
    );
    return data;
  },

  async updateAskFileReading(enabled: boolean): Promise<{ enabled: boolean; updated_by: string | null; updated_at: string | null }> {
    const { data } = await apiClient.put(
      "/api/admin/platform-config/ask-file-reading",
      { enabled },
      { headers: adminHeaders() }
    );
    return data;
  },

  // Phone/WhatsApp OTP: false forces every phone OTP anywhere (signup, login,
  // OAuth new-user step, profile phone-change) to the fixed code "0000" —
  // useful for QA/test environments without a live chatbase WhatsApp send.
  async getPhoneOtp(): Promise<{ enabled: boolean; updated_by: string | null; updated_at: string | null }> {
    const { data } = await apiClient.get<{ enabled: boolean; updated_by: string | null; updated_at: string | null }>(
      "/api/admin/platform-config/phone-otp",
      { headers: adminHeaders() }
    );
    return data;
  },

  async updatePhoneOtp(enabled: boolean): Promise<{ enabled: boolean; updated_by: string | null; updated_at: string | null }> {
    const { data } = await apiClient.put(
      "/api/admin/platform-config/phone-otp",
      { enabled },
      { headers: adminHeaders() }
    );
    return data;
  },

  async getWhatsappBoostConfig(): Promise<{
    enabled: boolean;
    template_name: string;
    template_language: string;
    callback_url: string;
    updated_by: string | null;
    updated_at: string | null;
  }> {
    const { data } = await apiClient.get(
      "/api/admin/platform-config/whatsapp-boost",
      { headers: adminHeaders() }
    );
    return data;
  },

  async updateWhatsappBoostConfig(body: {
    enabled: boolean;
    template_name: string;
    template_language: string;
    callback_url: string;
  }): Promise<{
    enabled: boolean;
    template_name: string;
    template_language: string;
    callback_url: string;
    updated_by: string | null;
    updated_at: string | null;
  }> {
    const { data } = await apiClient.put(
      "/api/admin/platform-config/whatsapp-boost",
      body,
      { headers: adminHeaders() }
    );
    return data;
  },

  // Storage backend routing: per-domain S3 vs Bunny Storage. New uploads
  // under a domain go to whichever backend is set; existing files keep working
  // either way since each stored key is tagged with the backend it was written to.
  async getStorageRouting(): Promise<{
    default_backend: string;
    domains: Record<string, string>;
    // Display-only registry metadata, keyed the same as `domains` — never
    // used for any routing decision, purely for grouping/labeling this panel.
    domain_groups: Record<string, string>;
    domain_labels: Record<string, string>;
    // ISO timestamp of the last time THIS domain's mode actually changed;
    // absent for a domain that's never been flipped since this was added.
    domain_updated_at: Record<string, string>;
    updated_by: string | null;
    updated_at: string | null;
  }> {
    const { data } = await apiClient.get(
      "/api/admin/platform-config/storage-routing",
      { headers: adminHeaders() }
    );
    return data;
  },

  // Flipping a domain TO bunny_storage (from anything else) also enqueues that
  // domain's migration on the backend, so a flipped domain never sits split
  // between S3 and Bunny waiting for a separate manual action. That job comes
  // back as `migration_job` — null when the flip needed none (s3 /
  // s3_via_bunny_cache targets, or a no-op re-save of a bunny_storage domain).
  async updateStorageRoutingDomain(domain: string, backend: StorageBackendMode): Promise<{
    default_backend: string;
    domains: Record<string, string>;
    domain_updated_at: Record<string, string>;
    updated_by: string | null;
    updated_at: string | null;
    migration_job: StorageMigrationJob | null;
  }> {
    const { data } = await apiClient.put(
      `/api/admin/platform-config/storage-routing/${domain}`,
      { backend },
      { headers: adminHeaders() }
    );
    return data;
  },

  // Circuit-breaker state of the three storage backends the read paths run on.
  // Pure in-memory read on the backend (no network calls), so it's cheap to poll.
  async getStorageHealth(): Promise<{
    s3: StorageBackendHealth;
    bunny_storage: StorageBackendHealth;
    bunny_cache: StorageBackendHealth;
  }> {
    const { data } = await apiClient.get(
      "/api/admin/storage-health",
      { headers: adminHeaders() }
    );
    return data;
  },

  // Queue a bunny_storage-routed domain's EXISTING objects to be moved onto
  // Bunny Storage. Returns immediately (202) with the job to poll — the copy
  // itself runs in a background worker, which copies only what's missing at
  // the target and tags each key only after verifying the bytes are there, and
  // never deletes the S3 original. Rejected with 400 for s3 /
  // s3_via_bunny_cache domains (nothing to move). `deduped` is true when the
  // domain already had a job in flight and this call joined it instead of
  // starting a second walk of the same collections.
  async enqueueStorageDomainMigration(
    domain: string
  ): Promise<{ job: StorageMigrationJob; deduped: boolean }> {
    const { data } = await apiClient.post(
      `/api/admin/storage-domains/${domain}/migrate`,
      null,
      { headers: adminHeaders() }
    );
    return data;
  },

  // Progress of a domain's migration: the active job, or the most recent
  // finished one if none is active (null if the domain was never migrated).
  async getStorageDomainMigrationStatus(
    domain: string
  ): Promise<{ domain: string; job: StorageMigrationJob | null }> {
    const { data } = await apiClient.get(
      `/api/admin/storage-domains/${domain}/migrate/status`,
      { headers: adminHeaders() }
    );
    return data;
  },

  // Fetch one real key from this domain, resolve it through the exact same
  // read-mode logic real traffic uses, and fetch one byte from the resulting
  // URL server-side — proof of what a user's browser would actually load
  // right now, not just what the mode toggle claims.
  async checkStorageDomainCacheDelivery(domain: string): Promise<StorageDomainCacheCheck> {
    const { data } = await apiClient.get(
      `/api/admin/storage-domains/${domain}/cache-check`,
      { headers: adminHeaders() }
    );
    return data;
  },

  // Recent migration jobs across every domain, newest first — used on mount so
  // a page reload picks up whatever is already in flight.
  async listStorageMigrationJobs(limit = 50): Promise<{ jobs: StorageMigrationJob[] }> {
    const { data } = await apiClient.get(
      "/api/admin/storage-domains/migrate/jobs",
      { headers: adminHeaders(), params: { limit } }
    );
    return data;
  },

  // Per-module, per-user key-path inventory (live scan, not a maintained index).
  async getStorageInventoryDomains(): Promise<{
    domains: { domain: string; collections: string[]; per_user: boolean; owner_label: string | null }[];
  }> {
    const { data } = await apiClient.get(
      "/api/admin/storage-inventory/domains",
      { headers: adminHeaders() }
    );
    return data;
  },

  async getStorageInventoryOwners(domain: string): Promise<{
    domain: string;
    owners: { id: string; name: string; email: string | null }[];
  }> {
    const { data } = await apiClient.get(
      `/api/admin/storage-inventory/${domain}/owners`,
      { headers: adminHeaders() }
    );
    return data;
  },

  async getStorageInventory(
    domain: string,
    params: { skip?: number; limit?: number; resolve_urls?: boolean; seller_name?: string } = {}
  ): Promise<{
    domain: string;
    collections: string[];
    owner_label: string | null;
    skip: number;
    limit: number;
    scanned_documents: number;
    total_documents_in_collections: number;
    backend_counts?: { s3: number; bunny: number };
    users: {
      owner_id: string;
      owner_name: string | null;
      documents: {
        collection: string;
        document_id: string;
        items: { field_path: string; key: string; backend: string; url?: string | null }[];
      }[];
    }[];
    error?: string;
  }> {
    const { data } = await apiClient.get(`/api/admin/storage-inventory/${domain}`, {
      headers: adminHeaders(),
      params,
    });
    return data;
  },

  // WhatsApp / chatbase call logs
  async getWhatsappLogs(params: { status?: string; call_type?: string; delivery_status?: string; date_from?: string; date_to?: string; limit?: number; skip?: number } = {}): Promise<{
    items: {
      id: string;
      call_type: string;
      endpoint: string;
      request_payload: Record<string, unknown>;
      status: string;
      response_body: Record<string, unknown> | null;
      error: string | null;
      campaign_id: string | null;
      phone_last4: string | null;
      delivery_status: string | null;
      delivery_error: string | null;
      delivery_updated_at: string | null;
      delivery_pending: boolean;
      created_at: string | null;
    }[];
    total: number;
  }> {
    const { data } = await apiClient.get("/api/admin/whatsapp/logs", { params, headers: adminHeaders() });
    return data;
  },

  async getWhatsappLogsSummary(): Promise<{ completed: number; failed: number; delivery_failed: number; delivery_pending: number; total: number }> {
    const { data } = await apiClient.get("/api/admin/whatsapp/logs/summary", { headers: adminHeaders() });
    return data;
  },

  async getWhatsappLogTypes(): Promise<{ items: { call_type: string; count: number }[] }> {
    const { data } = await apiClient.get("/api/admin/whatsapp/logs/types", { headers: adminHeaders() });
    return data;
  },

  async getChatbaseHealth(): Promise<{ ok: boolean; data?: Record<string, unknown>; error?: string }> {
    const { data } = await apiClient.get("/api/admin/whatsapp/health", { headers: adminHeaders() });
    return data;
  },

  // Voice agent lead-notify webhook call logs
  async getVoiceAgentLogs(params: { status?: string; limit?: number; skip?: number } = {}): Promise<{
    items: {
      id: string;
      call_type: string;
      endpoint: string;
      request_payload: Record<string, unknown>;
      status: string;
      response_body: Record<string, unknown> | null;
      error: string | null;
      campaign_id: string | null;
      phone_last4: string | null;
      delivery_status: string | null;
      delivery_updated_at: string | null;
      created_at: string | null;
    }[];
    total: number;
  }> {
    const { data } = await apiClient.get("/api/admin/voice-agent/logs", { params, headers: adminHeaders() });
    return data;
  },

  async getVoiceAgentLogsSummary(): Promise<{ completed: number; failed: number; total: number }> {
    const { data } = await apiClient.get("/api/admin/voice-agent/logs/summary", { headers: adminHeaders() });
    return data;
  },

  async getVoiceAgentConfig(): Promise<{
    configured: boolean;
    webhook_url: string | null;
    api_key_set: boolean;
    bot_id_set?: boolean;
    transport?: string;
    voice_agent_base_url?: string;
    // The runtime switch, separate from `configured` — a deployment can be
    // fully set up and deliberately switched off.
    calls?: {
      enabled: boolean;
      explicitly_set: boolean;
      updated_by: string | null;
      updated_at: string | null;
    };
  }> {
    const { data } = await apiClient.get("/api/admin/voice-agent/config", { headers: adminHeaders() });
    return data;
  },

  async getVoiceAgentQueuedLeads(params: { status?: string; limit?: number; skip?: number } = {}): Promise<{
    items: {
      id: string;
      lead_id: string | null;
      seller: string;
      deal: string;
      status: string;
      queued_at: string | null;
      dial_after: string | null;
      expires_at: string | null;
      attempts: number;
      reason: string;
      last_error: string | null;
    }[];
    total: number;
    counts: Record<string, number>;
  }> {
    const { data } = await apiClient.get("/api/admin/voice-agent/queued-leads", {
      params,
      headers: adminHeaders(),
    });
    return data;
  },

  async setVoiceAgentCallsEnabled(enabled: boolean): Promise<{
    enabled: boolean;
    updated_by: string;
    updated_at: string;
    configured: boolean;
  }> {
    const { data } = await apiClient.put(
      "/api/admin/voice-agent/calls-enabled",
      { enabled },
      { headers: adminHeaders() },
    );
    return data;
  },

  // Email call logs
  async getEmailLogs(params: { status?: string; call_type?: string; date_from?: string; date_to?: string; limit?: number; skip?: number } = {}): Promise<{
    items: {
      id: string;
      call_type: string;
      to: string;
      subject: string;
      status: string;
      error: string | null;
      campaign_id: string | null;
      lead_id: string | null;
      purpose: string | null;
      backend: string | null;
      endpoint: string | null;
      request_payload: Record<string, unknown> | null;
      response_body: unknown;
      created_at: string | null;
    }[];
    total: number;
  }> {
    const { data } = await apiClient.get("/api/admin/email/logs", { params, headers: adminHeaders() });
    return data;
  },

  async getEmailLogsSummary(): Promise<{ completed: number; failed: number; skipped: number; total: number }> {
    const { data } = await apiClient.get("/api/admin/email/logs/summary", { headers: adminHeaders() });
    return data;
  },

  async getEmailLogTypes(): Promise<{ items: { call_type: string; count: number }[] }> {
    const { data } = await apiClient.get("/api/admin/email/logs/types", { headers: adminHeaders() });
    return data;
  },

  async getEmailBackendStatus(): Promise<{ backend: string; configured: boolean }> {
    const { data } = await apiClient.get("/api/admin/email/status", { headers: adminHeaders() });
    return data;
  },

  // Verification gating
  async getVerificationGates(): Promise<{
    seller: { messaging?: boolean; deal_creation?: boolean; enquiry?: boolean; enquiry_receive?: boolean; ai_chat?: boolean; webinar?: boolean };
    buyer: { messaging?: boolean; enquiry?: boolean; ai_chat?: boolean; webinar?: boolean };
    gst_website_required?: { seller?: boolean; buyer?: boolean };
    updated_at: string | null;
  }> {
    const { data } = await apiClient.get("/api/admin/verification-gates", { headers: adminHeaders() });
    return data;
  },
  async updateVerificationGates(patch: {
    seller?: { messaging?: boolean; deal_creation?: boolean; enquiry?: boolean; enquiry_receive?: boolean; ai_chat?: boolean; webinar?: boolean };
    buyer?: { messaging?: boolean; enquiry?: boolean; ai_chat?: boolean; webinar?: boolean };
    gst_website_required?: { seller?: boolean; buyer?: boolean };
  }): Promise<{
    seller: { messaging?: boolean; deal_creation?: boolean; enquiry?: boolean; enquiry_receive?: boolean; ai_chat?: boolean; webinar?: boolean };
    buyer: { messaging?: boolean; enquiry?: boolean; ai_chat?: boolean; webinar?: boolean };
    gst_website_required?: { seller?: boolean; buyer?: boolean };
    updated_at: string | null;
  }> {
    const { data } = await apiClient.put("/api/admin/verification-gates", patch, { headers: adminHeaders() });
    return data;
  },

  // Per-user gate exemptions (admin User Detail "Verification Gating" tab).
  // Separate from the role-level toggles above: this exempts ONE user from
  // ONE named gate, leaving every other user and every other gate untouched.
  async getGateDefinitions(): Promise<{ gates: { key: string; label: string; description: string }[] }> {
    const { data } = await apiClient.get("/api/admin/gate-definitions", { headers: adminHeaders() });
    return data;
  },
  async updateUserGateExemption(userId: string, gateKey: string, exempt: boolean): Promise<any> {
    const { data } = await apiClient.put(
      `/api/admin/users/${userId}/gate-exemptions`,
      { gate_key: gateKey, exempt },
      { headers: adminHeaders() },
    );
    return data;
  },

  // Category pricing requirement settings
  async getPricingSettings(): Promise<{
    groups: Record<string, { slug: string; name: string; group_slug: string | null; pricing_required: boolean }[]>;
  }> {
    const { data } = await apiClient.get("/api/admin/settings/pricing", { headers: adminHeaders() });
    return data;
  },
  async updatePricingRequired(slug: string, pricingRequired: boolean): Promise<{ slug: string; pricing_required: boolean }> {
    const { data } = await apiClient.patch(`/api/admin/settings/pricing/${encodeURIComponent(slug)}`, { pricing_required: pricingRequired }, { headers: adminHeaders() });
    return data;
  },

  // Templates
  async getAdminTemplates(assetType: TemplateAssetType): Promise<{ templates: AdminTemplate[]; asset_type: string }> {
    const { data } = await apiClient.get<{ templates: AdminTemplate[]; asset_type: string }>(
      "/api/admin/templates",
      { params: { asset_type: assetType }, headers: adminHeaders() },
    );
    return data;
  },
  async getAdminTemplateCategories(): Promise<{ categories: AdminTemplateCategory[] }> {
    const { data } = await apiClient.get<{ categories: AdminTemplateCategory[] }>(
      "/api/admin/template-categories",
      { headers: adminHeaders() },
    );
    return data;
  },
  async createAdminTemplate(assetType: TemplateAssetType, formData: FormData): Promise<{ template: AdminTemplate; message: string }> {
    const { data } = await apiClient.post<{ template: AdminTemplate; message: string }>(
      `/api/admin/templates/${assetType}`,
      formData,
      { headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" } },
    );
    return data;
  },
  async updateAdminTemplate(assetType: TemplateAssetType, templateId: string, formData: FormData): Promise<{ template: AdminTemplate; message: string }> {
    const { data } = await apiClient.put<{ template: AdminTemplate; message: string }>(
      `/api/admin/templates/${assetType}/${templateId}`,
      formData,
      { headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" } },
    );
    return data;
  },
  /** Step 1 of admin "Generate Preview": sends a prompt, gets back the
   * Director's scene plan (no footage generated, no spend yet) so the admin
   * can review the revised/refined prompt per scene before confirming. */
  async planVideoTemplatePreview(
    prompt: string,
    opts?: { aspect_ratio?: string; duration_hint_seconds?: number },
  ): Promise<{ timeline: { scenes: Array<{ id: string; scene_prompt: string; [key: string]: unknown }> } }> {
    const { data } = await apiClient.post<{ timeline: { scenes: Array<{ id: string; scene_prompt: string; [key: string]: unknown }> } }>(
      "/api/admin/templates/video/plan-preview",
      { prompt, aspect_ratio: opts?.aspect_ratio, duration_hint_seconds: opts?.duration_hint_seconds },
      { headers: adminHeaders() },
    );
    return data;
  },
  /** Step 2, submit half: kicks off real footage sourcing for an approved
   * timeline (from planVideoTemplatePreview above) as a background job and
   * returns immediately with a preview_id — real generation can take
   * several minutes, longer than this server's reverse-proxy will hold a
   * connection open, so the result is never awaited synchronously. Poll
   * getVideoTemplatePreviewStatus(preview_id) for the result. */
  async startVideoTemplatePreview(timeline: unknown): Promise<{ preview_id: string; status: string }> {
    const { data } = await apiClient.post<{ preview_id: string; status: string }>(
      "/api/admin/templates/video/generate-preview",
      { timeline },
      { headers: adminHeaders() },
    );
    return data;
  },
  /** Step 2, poll half — see startVideoTemplatePreview above. `status` is
   * "pending" | "completed" | "failed"; other fields only populate once
   * status is "completed". video_s3_key/preview_s3_key pass straight
   * through to create/update — no re-upload needed. */
  async getVideoTemplatePreviewStatus(previewId: string): Promise<{
    preview_id: string; status: string; output_url: string; job_id: string; scenes: unknown[];
    video_s3_key: string; video_url: string; preview_s3_key: string; preview_url: string; error: string;
  }> {
    const { data } = await apiClient.get<{
      preview_id: string; status: string; output_url: string; job_id: string; scenes: unknown[];
      video_s3_key: string; video_url: string; preview_s3_key: string; preview_url: string; error: string;
    }>(
      `/api/admin/templates/video/generate-preview/${encodeURIComponent(previewId)}`,
      { headers: adminHeaders() },
    );
    return data;
  },
  async extractItineraryHtml(html: string): Promise<{ base_prompt: string; prompt_template: string; fields: AdminTemplateField[]; lint_warnings: string[] }> {
    const fd = new FormData();
    fd.append("html_template_content", html);
    const { data } = await apiClient.post<{ base_prompt: string; prompt_template: string; fields: AdminTemplateField[]; lint_warnings: string[] }>(
      "/api/admin/templates/itinerary/extract-html",
      fd,
      { headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" } },
    );
    return data;
  },
  async deleteAdminTemplate(assetType: TemplateAssetType, templateId: string): Promise<any> {
    const { data } = await apiClient.delete(`/api/admin/templates/${assetType}/${templateId}`, { headers: adminHeaders() });
    return data;
  },
  async getAdminUserAssets(params: { asset_type?: string; user_id?: string; search?: string; skip?: number; limit?: number } = {}): Promise<AdminUserAssetsResponse> {
    const { data } = await apiClient.get<AdminUserAssetsResponse>("/api/admin/user-assets", { params, headers: adminHeaders() });
    return data;
  },
  async useAssetAsTemplate(body: { user_id: string; session_id: string; s3_key: string; asset_type: string }): Promise<AdminTemplateFromAssetDraft> {
    const { data } = await apiClient.post<AdminTemplateFromAssetDraft>("/api/admin/templates/from-asset", body, { headers: adminHeaders() });
    return data;
  },
  async getAdminPrompts(): Promise<AdminPromptsResponse> {
    const { data } = await apiClient.get<AdminPromptsResponse>("/api/admin/prompts", { headers: adminHeaders() });
    return data;
  },
  async runAdminPrompt(body: AdminPromptRunRequest): Promise<AdminPromptRunResponse> {
    const { data } = await apiClient.post<AdminPromptRunResponse>("/api/admin/prompts/run", body, { headers: adminHeaders() });
    return data;
  },
  async extractPromptFile(file: File): Promise<{ file_type: string; filename: string; text?: string; image_data_urls?: string[] }> {
    const form = new FormData();
    form.append("file", file);
    const { data } = await apiClient.post("/api/admin/prompts/extract-file", form, {
      headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" },
    });
    return data;
  },

  // Help / Support KB (buyer/seller RAG help-doc source + indexing status)
  async getHelpKbStatus(target: HelpKbTarget): Promise<HelpKbStatus> {
    const { data } = await apiClient.get<HelpKbStatus>(`/api/admin/help-kb/${target}`, { headers: adminHeaders() });
    return data;
  },
  async saveHelpKbText(target: HelpKbTarget, text: string): Promise<HelpKbStatus> {
    const { data } = await apiClient.post<HelpKbStatus>(`/api/admin/help-kb/${target}/text`, { text }, { headers: adminHeaders() });
    return data;
  },
  async saveHelpKbPdf(target: HelpKbTarget, file: File): Promise<HelpKbStatus> {
    const form = new FormData();
    form.append("file", file);
    const { data } = await apiClient.post<HelpKbStatus>(`/api/admin/help-kb/${target}/pdf`, form, {
      headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" },
    });
    return data;
  },
  async reindexHelpKb(target: HelpKbTarget): Promise<HelpKbReindexResult> {
    const { data } = await apiClient.post<HelpKbReindexResult>(`/api/admin/help-kb/${target}/reindex`, {}, { headers: adminHeaders() });
    return data;
  },
  async savePromptOverride(promptId: string, body: {
    system_prompt: string;
    user_prompt: string;
    model?: string | null;
    /** Full ordered model-chain override for image-generation entries — a
     *  REPLACEMENT for the coded chain, not a prepend. */
    model_chain?: string[] | null;
    temperature?: number | null;
    max_tokens?: number | null;
    note?: string;
  }): Promise<{ ok: boolean; prompt_id: string; updated_at: string }> {
    const { data } = await apiClient.put(`/api/admin/prompts/${promptId}/override`, body, { headers: adminHeaders() });
    return data;
  },
  async getPromptOverride(
    promptId: string,
  ): Promise<{ prompt_id: string; override: { system_prompt: string; user_prompt: string; model?: string | null; model_chain?: string[] | null; temperature?: number | null; max_tokens?: number | null; updated_at?: string; updated_by?: string } | null }> {
    const { data } = await apiClient.get(`/api/admin/prompts/${promptId}/override`, { headers: adminHeaders() });
    return data;
  },
  // Past versions of this prompt's override — what it was, when it changed, who changed it.
  // entries[0] is the most recent PREVIOUS state (snapshotted right before the current save).
  async getPromptHistory(promptId: string): Promise<{
    prompt_id: string;
    history: Array<{
      replaced_at: string;
      snapshot: { system_prompt?: string; user_prompt?: string; model?: string | null; model_chain?: string[] | null; temperature?: number | null; max_tokens?: number | null };
    }>;
  }> {
    const { data } = await apiClient.get(`/api/admin/prompts/${promptId}/history`, { headers: adminHeaders() });
    return data;
  },
  // Promote the saved draft to production (the store the live pipeline reads).
  async publishPromptOverride(promptId: string): Promise<{ ok: boolean; prompt_id: string; published_at: string }> {
    const { data } = await apiClient.post(`/api/admin/prompts/${promptId}/publish`, {}, { headers: adminHeaders() });
    return data;
  },
  // Remove the prompt from production — live traffic reverts to the code constant.
  async unpublishPromptOverride(promptId: string): Promise<{ ok: boolean; unpublished: boolean }> {
    const { data } = await apiClient.delete(`/api/admin/prompts/${promptId}/publish`, { headers: adminHeaders() });
    return data;
  },
  // Fetch the current production override for a prompt (null if not published).
  async getPublishedPromptOverride(promptId: string): Promise<{ prompt_id: string; published: import("./types").AdminPromptDbOverride | null }> {
    const { data } = await apiClient.get(`/api/admin/prompts/${promptId}/publish`, { headers: adminHeaders() });
    return data;
  },
  async streamAdminRagChat(
    message: string,
    sessionId: string | null,
    onSession: (sid: string) => void,
    onToken: (text: string) => void,
    onMetadata: (data: Record<string, unknown>) => void,
    onDone: () => void,
    userRole: "buyer" | "seller" = "buyer",
  ): Promise<void> {
    const headers = adminHeaders() as Record<string, string>;
    headers["Content-Type"] = "application/json";
    const res = await fetch(`${env.apiBaseUrl}/api/admin/prompts/rag-playground/chat`, {
      method: "POST",
      headers,
      body: JSON.stringify({ message, session_id: sessionId ?? null, user_role: userRole }),
    });
    if (!res.ok || !res.body) {
      const text = await res.text().catch(() => "Admin RAG chat request failed");
      throw new Error(text);
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const frames = buf.split("\n\n");
      buf = frames.pop() ?? "";
      for (const frame of frames) {
        if (!frame.trim()) continue;
        const lines = frame.split("\n");
        let eventType = "";
        let dataStr = "";
        for (const line of lines) {
          if (line.startsWith("event: ")) eventType = line.slice(7).trim();
          else if (line.startsWith("data: ")) dataStr = line.slice(6).trim();
        }
        if (!eventType || !dataStr) continue;
        try {
          const data = JSON.parse(dataStr) as Record<string, unknown>;
          if (eventType === "session") onSession(data.session_id as string);
          else if (eventType === "token") onToken((data.text as string) ?? "");
          else if (eventType === "metadata") onMetadata(data);
          else if (eventType === "done") onDone();
          else if (eventType === "error") throw new Error((data.message as string) ?? "Stream error");
        } catch {
          // ignore parse errors on individual frames
        }
      }
    }
    onDone();
  },
  async clearAdminRagSession(sessionId: string): Promise<void> {
    await apiClient.delete(`/api/admin/prompts/rag-playground/session/${sessionId}`, { headers: adminHeaders() });
  },
  async getDealDetailForPlayground(dealId: string): Promise<Record<string, unknown>> {
    const { data } = await apiClient.get(`/api/admin/prompts/deal-detail/${dealId}`, { headers: adminHeaders() });
    return data;
  },
  async listAdminRagSessions(): Promise<{ session_id: string; preview: string; message_count: number; updated_at: string }[]> {
    const { data } = await apiClient.get("/api/admin/prompts/rag-playground/sessions", { headers: adminHeaders() });
    return data.sessions ?? [];
  },
  async getAdminRagSession(sessionId: string): Promise<{ session_id: string; messages: Record<string, unknown>[] }> {
    const { data } = await apiClient.get(`/api/admin/prompts/rag-playground/sessions/${sessionId}`, { headers: adminHeaders() });
    return data;
  },
  // Market Research chat playground — single request/response turn (no streaming;
  // the real endpoint isn't SSE either, it's one Perplexity call and back).
  // draftOverrides (keyed by prompt_id) sends the CURRENT UNSAVED Prompt Playground
  // editor state so this chat turn tests your edit before you click Update Live —
  // same idea as Test/Run, just for a full conversation. Never written to Mongo.
  async runMarketResearchPlaygroundChat(
    message: string,
    sessionId: string | null,
    draftOverrides?: Record<string, { system_prompt?: string; user_prompt?: string; model?: string | null; temperature?: number | null; max_tokens?: number | null }>,
  ): Promise<{ message: string; session_id: string }> {
    const { data } = await apiClient.post(
      "/api/admin/prompts/market-research-playground/chat",
      { message, session_id: sessionId ?? null, draft_overrides: draftOverrides ?? null },
      { headers: adminHeaders() },
    );
    return data;
  },
  async listMarketResearchPlaygroundSessions(): Promise<{ session_id: string; preview: string; message_count: number; updated_at: string }[]> {
    const { data } = await apiClient.get("/api/admin/prompts/market-research-playground/sessions", { headers: adminHeaders() });
    return data.sessions ?? [];
  },
  async getMarketResearchPlaygroundSession(sessionId: string): Promise<{ session_id: string; messages: Record<string, unknown>[] }> {
    const { data } = await apiClient.get(`/api/admin/prompts/market-research-playground/sessions/${sessionId}`, { headers: adminHeaders() });
    return data;
  },
  async clearMarketResearchPlaygroundSession(sessionId: string): Promise<void> {
    await apiClient.delete(`/api/admin/prompts/market-research-playground/session/${sessionId}`, { headers: adminHeaders() });
  },
  async runDealAnalysisPlayground(body: {
    title: string;
    description: string;
    document_text: string;
    image_urls: string[];
    is_video: boolean;
    model?: string | null;
  }): Promise<{ output: string; model: string; usage: Record<string, number>; prompt_used: string }> {
    const { data } = await apiClient.post("/api/admin/prompts/deal-analysis-playground/run", body, { headers: adminHeaders() });
    return data;
  },
  async runAiMediaPlayground(body: {
    generation_type: string;
    user_message: string;
    variables: Record<string, string>;
    model?: string | null;
  }): Promise<{ output: string; model: string; usage: Record<string, number>; prompt_id: string; used_db_override: boolean }> {
    const { data } = await apiClient.post("/api/admin/prompts/ai-media-playground/run", body, { headers: adminHeaders() });
    return data;
  },

  // Gallery media moderation
  async getGalleryReviewItems(collection: string, dealId: string, field: "gallery" | "preview_pages_urls"): Promise<GalleryItem[]> {
    const { data } = await apiClient.get<GalleryItem[]>(
      `/api/admin/deals/${encodeURIComponent(collection)}/${encodeURIComponent(dealId)}/media-review/${field}`,
      { headers: adminHeaders() },
    );
    return data;
  },
  async approveGalleryItem(collection: string, dealId: string, field: string, key: string): Promise<void> {
    await apiClient.post(
      `/api/admin/deals/${encodeURIComponent(collection)}/${encodeURIComponent(dealId)}/media-review/${field}/${encodeURIComponent(key)}/approve`,
      {},
      { headers: adminHeaders() },
    );
  },
  async rejectGalleryItem(collection: string, dealId: string, field: string, key: string, reason: string): Promise<void> {
    await apiClient.post(
      `/api/admin/deals/${encodeURIComponent(collection)}/${encodeURIComponent(dealId)}/media-review/${field}/${encodeURIComponent(key)}/reject`,
      { reason },
      { headers: adminHeaders() },
    );
  },
  async reapproveGalleryItem(collection: string, dealId: string, field: string, key: string): Promise<void> {
    await apiClient.post(
      `/api/admin/deals/${encodeURIComponent(collection)}/${encodeURIComponent(dealId)}/media-review/${field}/${encodeURIComponent(key)}/reapprove`,
      {},
      { headers: adminHeaders() },
    );
  },
  /** Permanently removes the item from the deal's gallery/preview_pages_urls array
   *  (both this collection and its typed<->shadow sibling) and deletes the S3 object.
   *  Unlike reject, this can't be undone. */
  async deleteGalleryItem(collection: string, dealId: string, field: string, key: string): Promise<void> {
    await apiClient.delete(
      `/api/admin/deals/${encodeURIComponent(collection)}/${encodeURIComponent(dealId)}/media-review/${field}/${encodeURIComponent(key)}`,
      { headers: adminHeaders() },
    );
  },
  /** Permanently deletes ONE of a deal's original-asset fields ("image" | "video" |
   *  "pdf" | "document") — the S3 object and the referencing fields, in this
   *  collection and its typed<->shadow sibling. Leaves every other original field,
   *  and the AI-generated flyer, untouched. */
  async deleteOriginalAsset(collection: string, dealId: string, field: "image" | "video" | "pdf" | "document"): Promise<{ deleted_key: string }> {
    const { data } = await apiClient.delete(
      `/api/admin/deals/${encodeURIComponent(collection)}/${encodeURIComponent(dealId)}/original/${field}`,
      { headers: adminHeaders() },
    );
    return data;
  },
  /** Permanently deletes a deal's AI-generated flyer — the S3 object and every
   *  referencing field, in this collection and its typed<->shadow sibling. Does not
   *  touch the original asset or gallery items. */
  async deleteAiFlyer(collection: string, dealId: string): Promise<{ deleted_keys: string[] }> {
    const { data } = await apiClient.delete(
      `/api/admin/deals/${encodeURIComponent(collection)}/${encodeURIComponent(dealId)}/ai-flyer`,
      { headers: adminHeaders() },
    );
    return data;
  },
  async getMediaDownloadUrl(urlOrKey: string, filename: string): Promise<string> {
    const { data } = await apiClient.get<{ url: string }>("/api/admin/media-review/download-url", {
      params: { url_or_key: urlOrKey, filename },
      headers: adminHeaders(),
    });
    return data.url;
  },
  /** Replace a deal's original asset (image/video/pdf/docx); image/pdf/docx re-run AI flyer synthesis, video just swaps the clip. */
  async replaceFlyerImage(
    collection: string,
    dealId: string,
    file: File,
  ): Promise<{ key: string; display_url: string; kind: "image" | "video" | "pdf" | "docx" }> {
    const formData = new FormData();
    formData.append("file", file);
    const { data } = await apiClient.post(
      `/api/admin/deals/${encodeURIComponent(collection)}/${encodeURIComponent(dealId)}/flyer-image`,
      formData,
      { headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" } },
    );
    return data;
  },
  /** Directly overrides the AI-generated flyer with an admin-supplied image
   *  (e.g. from any external image generator), replacing ai_generated_flyer +
   *  image_clean (and their old S3 objects). Permanently locks the flyer —
   *  no auto-regeneration will overwrite it again until deleteAiFlyer clears
   *  the lock. */
  async uploadAiFlyer(collection: string, dealId: string, file: File): Promise<{ key: string; locked: boolean; display_url: string }> {
    const formData = new FormData();
    formData.append("file", file);
    const { data } = await apiClient.post(
      `/api/admin/deals/${encodeURIComponent(collection)}/${encodeURIComponent(dealId)}/ai-flyer/upload`,
      formData,
      { headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" } },
    );
    return data;
  },
  /** Same response shape as getDeal(id), but reads the exact typed collection
   *  the caller already knows instead of the shadow-first lookup getDeal does.
   *  A promoted deal has a doc in both the shadow `deals` collection and its
   *  typed collection, and getDeal(id) always returns the shadow one — which
   *  has AI-derived fields (enriched_summary, ai_extracted, full pricing/
   *  destination/etc.) stripped after promotion. Use this whenever the
   *  collection is already known (which it always is on the Deals page), e.g.
   *  refetching after an action like flyer generation. */
  async getDealInCollection(collection: string, dealId: string): Promise<any> {
    const { data } = await apiClient.get(
      `/api/admin/deals/${encodeURIComponent(collection)}/${encodeURIComponent(dealId)}/detail`,
      { headers: adminHeaders() },
    );
    return data;
  },
  /** Fires a single on-demand, category-template-based AI flyer generation for one
   *  deal. Returns immediately with {status: "running"} — the deal's
   *  `flyer_auto_status` field is what flips to done/failed when the background
   *  job lands (poll it via pollFlyerStatus). */
  // ── Deals deleted by sellers ──────────────────────────────────────
  //
  // A seller's delete archives the full deal into `deal_delete_by_seller`
  // rather than destroying it, so these read that archive. An admin's own
  // delete has always been permanent, and purgeSellerDeletedDeal below is
  // the permanent delete for an archived row.

  async listSellerDeletedDeals(params: {
    page?: number; page_size?: number; seller_id?: string; search?: string; collection?: string;
  }): Promise<SellerDeletedDealsResponse> {
    const { data } = await apiClient.get<SellerDeletedDealsResponse>(
      "/api/admin/deals/deleted-by-seller", { params, headers: adminHeaders() },
    );
    return data;
  },
  async getSellerDeletionStats(params: { days?: number; limit?: number } = {}): Promise<SellerDeletionStat[]> {
    const { data } = await apiClient.get<SellerDeletionStat[]>(
      "/api/admin/deals/deleted-by-seller/stats", { params, headers: adminHeaders() },
    );
    return data;
  },
  /** Full archived document for the detail drawer — everything the deal had
   *  when it was deleted, media presigned. */
  async getSellerDeletedDeal(dealId: string): Promise<any> {
    const { data } = await apiClient.get(
      `/api/admin/deals/deleted-by-seller/${encodeURIComponent(dealId)}/detail`,
      { headers: adminHeaders() },
    );
    return data;
  },
  /** PERMANENT — destroys the archived deal and its S3 assets. Unrecoverable. */
  async purgeSellerDeletedDeal(dealId: string): Promise<{ detail: string }> {
    const { data } = await apiClient.delete<{ detail: string }>(
      `/api/admin/deals/deleted-by-seller/${encodeURIComponent(dealId)}`,
      { headers: adminHeaders() },
    );
    return data;
  },
  async generateAiFlyer(collection: string, dealId: string): Promise<any> {
    const { data } = await apiClient.post(
      `/api/admin/deals/${encodeURIComponent(collection)}/${encodeURIComponent(dealId)}/generate-ai-flyer`,
      {},
      { headers: adminHeaders() },
    );
    return data;
  },

  // ── Organizations (Phase 1, superadmin side) ───────────────────────────
  // Mounted under /api/admin so AdminAuditMiddleware records every write.

  /** Clustering tool. Returns groups of users sharing one company signal —
   *  never auto-creates anything; superadmin reviews and confirms. */
  async searchOrgCandidates(params: {
    agency_name?: string;
    gst_number?: string;
    business_registration_id?: string;
    company_email_domain?: string;
    website?: string;
  }): Promise<OrgCandidateSearchResponse> {
    const { data } = await apiClient.get<OrgCandidateSearchResponse>("/api/admin/org-candidates", {
      params,
      headers: adminHeaders(),
    });
    return data;
  },

  /** Who's actually in this org's candidate pool right now (the directory
   *  page only shows a count). */
  async getOrgCandidatesList(orgId: string): Promise<OrgCandidateUser[]> {
    const { data } = await apiClient.get<OrgCandidateUser[]>(
      `/api/admin/organizations/${encodeURIComponent(orgId)}/candidates`,
      { headers: adminHeaders() },
    );
    return data;
  },

  /** Every ACTIVE member — admins and plain members both. An active plain
   *  member otherwise has no visible list anywhere in this console. */
  async getOrgMembersList(orgId: string): Promise<OrgCandidateUser[]> {
    const { data } = await apiClient.get<OrgCandidateUser[]>(
      `/api/admin/organizations/${encodeURIComponent(orgId)}/members`,
      { headers: adminHeaders() },
    );
    return data;
  },

  async getOrganizations(status?: string): Promise<OrganizationSummary[]> {
    const { data } = await apiClient.get<OrganizationSummary[]>("/api/admin/organizations", {
      params: status ? { status } : undefined,
      headers: adminHeaders(),
    });
    return data;
  },

  async createOrganization(body: OrganizationCreateRequest): Promise<OrganizationSummary> {
    const { data } = await apiClient.post<OrganizationSummary>("/api/admin/organizations", body, {
      headers: adminHeaders(),
    });
    return data;
  },

  /** Name / seat_limit — superadmin-only by design. */
  async patchOrganization(orgId: string, body: OrganizationPatchRequest): Promise<OrganizationSummary> {
    const { data } = await apiClient.patch<OrganizationSummary>(
      `/api/admin/organizations/${encodeURIComponent(orgId)}`,
      body,
      { headers: adminHeaders() },
    );
    return data;
  },

  async addOrgCandidates(orgId: string, userIds: string[]): Promise<{ org_id: string; added: string[] }> {
    const { data } = await apiClient.post<{ org_id: string; added: string[] }>(
      `/api/admin/organizations/${encodeURIComponent(orgId)}/candidates`,
      { user_ids: userIds },
      { headers: adminHeaders() },
    );
    return data;
  },

  async removeOrgCandidate(orgId: string, userId: string): Promise<{ removed: boolean }> {
    const { data } = await apiClient.delete<{ removed: boolean }>(
      `/api/admin/organizations/${encodeURIComponent(orgId)}/candidates/${encodeURIComponent(userId)}`,
      { headers: adminHeaders() },
    );
    return data;
  },

  async suspendOrganization(orgId: string): Promise<OrganizationSummary> {
    const { data } = await apiClient.post<OrganizationSummary>(
      `/api/admin/organizations/${encodeURIComponent(orgId)}/suspend`,
      {},
      { headers: adminHeaders() },
    );
    return data;
  },

  async reactivateOrganization(orgId: string): Promise<OrganizationSummary> {
    const { data } = await apiClient.post<OrganizationSummary>(
      `/api/admin/organizations/${encodeURIComponent(orgId)}/reactivate`,
      {},
      { headers: adminHeaders() },
    );
    return data;
  },

  /** Mints a one-off login link that drops the given Org Admin straight into
   *  the org-admin console. Superadmin copies it and sends it to them. */
  async createOrgAdminMagicLink(orgId: string, userId: string): Promise<OrgAdminMagicLinkResponse> {
    const { data } = await apiClient.post<OrgAdminMagicLinkResponse>(
      `/api/admin/organizations/${encodeURIComponent(orgId)}/admins/${encodeURIComponent(userId)}/magic-link`,
      {},
      { headers: adminHeaders() },
    );
    return data;
  },

  /** Promotes an existing active member to Org Admin. */
  async promoteOrgAdmin(orgId: string, userId: string): Promise<OrganizationSummary> {
    const { data } = await apiClient.post<OrganizationSummary>(
      `/api/admin/organizations/${encodeURIComponent(orgId)}/admins/${encodeURIComponent(userId)}`,
      {},
      { headers: adminHeaders() },
    );
    return data;
  },

  /** Demotes an Org Admin back to a plain member. Backend rejects demoting
   *  the org's last remaining admin. */
  async demoteOrgAdmin(orgId: string, userId: string): Promise<OrganizationSummary> {
    const { data } = await apiClient.delete<OrganizationSummary>(
      `/api/admin/organizations/${encodeURIComponent(orgId)}/admins/${encodeURIComponent(userId)}`,
      { headers: adminHeaders() },
    );
    return data;
  },
};
